/**
 * ScanModule.jsx — Nirvikalp 4.0
 *
 * Single unified screen for:
 *   Plan → Lays → Barcodes → Print → Scan
 *
 * Cut plan / lay / bundle / barcode generation logic is UNCHANGED
 * from ProductionMonitor.jsx. Only the layout, print tickets and
 * scan live-efficiency panel are new.
 *
 * Props:   { db, pmDb, setPmDb, apiLive }
 * Same props as CutPlan + PrintScan in the original file.
 * Replace those two tabs with this single component.
 *
 * Usage in ProductionMonitor root:
 *   import ScanModule from './ScanModule';
 *   ...
 *   { tab==='scanmodule' && <ScanModule db={db} pmDb={pmDb} setPmDb={setPmDb} apiLive={apiLive} /> }
 */

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Barcode, BarcodeCard } from './BarcodeRenderer';

// ── re-use constants from ProductionMonitor context ──────────────────────────
const PM_API = 'https://nirvikalp.integrashion.com/api/pm_api.php';

const LS = {
  get: k  => { try { return JSON.parse(localStorage.getItem(`pm_${k}`)) || null; } catch { return null; } },
  set: (k,v) => { try { localStorage.setItem(`pm_${k}`, JSON.stringify(v)); } catch {} },
};

// Barcode value generators — SHORT NUMERIC IDs for scannable print output
// Matches the sample PDF's format (e.g. 1000000009239)
// Base = timestamp seed so IDs stay unique across sessions
const BC_BASE = 1000000000000; // 13-digit base
const BC = {
  // Short numeric: base + encoded index — stays under 13 digits, pure numeric = slimmest barcode
  line:     (factId, lineId) => String(BC_BASE + parseInt(String(factId).slice(-4)) * 10000 + parseInt(String(lineId).replace(/\D/g,'').slice(-4))||0),
  worker:   opId => String(BC_BASE + parseInt(String(opId).replace(/\D/g,'').slice(-6))||0),
  period:   (date, lineId, no) => {
    const d = date.replace(/-/g,'').slice(-6); // YYMMDD
    return String(1000000000 + parseInt(d) * 1000 + parseInt(String(lineId).replace(/\D/g,'').slice(-1)||'0') * 100 + no);
  },
  bundle:   (planId, layNo, bndNo) => String(BC_BASE + (planId % 10000000) * 1000 + layNo * 100 + bndNo),
  opBundle: (planId, layNo, bndNo, opIdx) => String(BC_BASE + (planId % 1000000) * 10000 + layNo * 1000 + bndNo * 100 + opIdx),
  // Garment unit — ONE permanent QR per physical piece, from cutting through
  // consumer. Dedicated numeric range (prefix 3) so it never collides with
  // bundle/operation/line/worker barcodes. Deterministic from plan+lay+
  // bundle+piece so it never needs regenerating differently across sessions.
  garmentUnit: (planId, layNo, bndNo, pieceNo) =>
    String(3000000000000 + (planId % 100000) * 100000 + layNo * 10000 + bndNo * 100 + pieceNo),
  lostTime: (code) => String(9000000000000 + parseInt(String(code).replace(/\D/g,''))),
};

// ── QR CODE — Qaram interoperability layer ──────────────────────────────────
// Qaram (a separate Integrashion platform) scans QR codes in a pipe-delimited
// format: CYCLE|id|name  ·  PROC|cycleId|procId|order|name  ·  LOC|id|name|type
// ·  PROD|sku|style|size|brand. Generating our tickets in these same formats
// means a Qaram scanner can read our Line/Period/Bundle tickets natively —
// this is genuine interop, not just a cosmetic QR option.
//
// QR images are rendered via the same public image API Qaram itself uses
// (api.qrserver.com) — an <img> tag, not a client-side JS library. This
// sidesteps the CDN MIME-type failures we hit earlier with local QR libs.
// ecc: 'L'|'M'|'Q'|'H' — error correction level. 'H' (30% damage recovery)
// is used for garment unit QRs since heat-transfer + repeated washing
// degrades the print; other tickets (paper, indoor) use the default 'M'.
const qrUrl = (data, size=150, ecc='M') =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}&margin=3&ecc=${ecc}`;

// Our own barcode value is always placed at index 1 (right after the type
// tag) in every payload we generate, so extraction on scan is uniform.
const QR = {
  line:     (barcode, lineName)               => `LOC|${barcode}|${lineName}|LINE`,
  period:   (barcode, periodNo, date)          => `CYCLE|${barcode}|Period ${periodNo} (${date})|Production`,
  worker:   (barcode, name, designation)       => `WORKER|${barcode}|${name}|${designation||''}`,
  bundleOp: (barcode, styleRef, size, opName)  => `PROD|${barcode}|${styleRef||''}|${size||''}|${opName||''}`,
  lostTime: (barcode, code, label)             => `LOST|${barcode}|${code}|${label}`,
};

// Scanning either our own numeric barcode OR a Qaram-style pipe-delimited
// QR should resolve to the same underlying record. This extracts the real
// barcode value regardless of which format was scanned.
function extractScanValue(raw) {
  const trimmed = raw.trim();
  if (trimmed.includes('|')) {
    const parts = trimmed.split('|');
    return { value: parts[1] || trimmed, qrType: parts[0], raw: trimmed };
  }
  return { value: trimmed, qrType: null, raw: trimmed };
}

// Lost time reason codes — standard garment industry L1–L12
// Represents time a WORKER could not work due to factors outside their control.
// Charged against SECTION efficiency, NOT the individual worker's efficiency.
const DEFAULT_LOST_TIME_REASONS = [
  { code:'L1',  label:'Machine Breakdown' },
  { code:'L2',  label:'No Work / Material' },
  { code:'L3',  label:'Power Failure' },
  { code:'L4',  label:'Quality Check Hold' },
  { code:'L5',  label:'Training' },
  { code:'L6',  label:'Meeting' },
  { code:'L7',  label:'Line Changeover' },
  { code:'L8',  label:'Re-work' },
  { code:'L9',  label:'Needle Change' },
  { code:'L10', label:'Thread Change' },
  { code:'L11', label:'Maintenance' },
  { code:'L12', label:'Other' },
];

const pmApi = {
  async save(table, data) {
    try {
      const r = await fetch(PM_API, { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'save', table, data }) });
      return await r.json();
    } catch { return { success:false }; }
  },
  async batchSave(table, rows) {
    try {
      const r = await fetch(PM_API, { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'batch_save', table, rows }) });
      return await r.json();
    } catch { return { success:false }; }
  },
  async processScan(payload) {
    try {
      const r = await fetch(PM_API, { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action:'process_scan', ...payload }) });
      return await r.json();
    } catch { return { success:false }; }
  },
  async call(action, payload={}) {
    try {
      const r = await fetch(PM_API, { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action, ...payload }) });
      return await r.json();
    } catch (e) { return { success:false, error:String(e) }; }
  },
  async batchCall(action, items) {
    try {
      const r = await fetch(PM_API, { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ action, items }) });
      return await r.json();
    } catch (e) { return { success:false, error:String(e) }; }
  },
};

// ── Camera scanner — uses QuaggaJS for reliable Code128/1D barcode detection
// html5-qrcode works for QR but is poor at 1D barcodes. Quagga is built for them.
let quaggaLoading = false;
function isQuaggaReady() { return typeof window !== 'undefined' && !!window.Quagga; }
function loadQuagga(srcs, idx = 0) {
  if (idx >= srcs.length) { console.error('All Quagga CDN sources failed'); return; }
  const src = srcs[idx];
  if (document.querySelector(`script[src="${src}"]`)) {
    if (isQuaggaReady()) window.dispatchEvent(new Event('quagga-ready'));
    return;
  }
  const s = document.createElement('script');
  s.src = src; s.async = true;
  s.onload = () => window.dispatchEvent(new Event('quagga-ready'));
  s.onerror = () => { s.remove(); loadQuagga(srcs, idx + 1); };
  document.head.appendChild(s);
}
function ensureQuagga() {
  if (isQuaggaReady() || quaggaLoading) return;
  quaggaLoading = true;
  loadQuagga([
    'https://unpkg.com/quagga@0.12.1/dist/quagga.min.js',
    'https://cdn.jsdelivr.net/npm/quagga@0.12.1/dist/quagga.min.js',
  ]);
}

// ── Camera scanner overlay component ──────────────────────────────────────
// batchMode: when true, accumulates unique codes instead of firing each
// immediately — lets operator hold camera over a full printed page and
// submit every detected barcode at once instead of scanning one by one.
function CameraScanner({ onResult, onClose, batchMode=false, onBatchSubmit }) {
  const videoRef     = useRef(null);
  const mountedRef   = useRef(true);
  const streamRef    = useRef(null);
  const runningRef   = useRef(false);
  const lastScanRef  = useRef('');
  const [libReady,  setLibReady]  = useState(isQuaggaReady());
  const [scanState, setScanState] = useState('loading');
  const [errMsg,    setErrMsg]    = useState('');
  const [lastScan,  setLastScan]  = useState('');
  const [batchCodes, setBatchCodes] = useState([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, []);

  const cleanup = () => {
    // Stop Quagga
    if (runningRef.current && window.Quagga) {
      try { window.Quagga.stop(); } catch {}
      runningRef.current = false;
    }
    // Stop camera stream tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  // Step 1: load Quagga library
  useEffect(() => {
    if (libReady) { setScanState('starting'); return; }
    const onLib = () => { if (mountedRef.current) { setLibReady(true); setScanState('starting'); } };
    window.addEventListener('quagga-ready', onLib);
    ensureQuagga();
    if (isQuaggaReady()) { setLibReady(true); setScanState('starting'); }
    return () => window.removeEventListener('quagga-ready', onLib);
  }, [libReady]);

  // Step 2: once library ready, start getUserMedia directly
  useEffect(() => {
    if (scanState !== 'starting' || !libReady) return;

    cleanup();

    const tryStart = async (facingMode) => {
      // Wait up to 500ms for the video element to appear in DOM
      let attempts = 0;
      while (!videoRef.current && attempts < 10) {
        await new Promise(r => setTimeout(r, 50));
        attempts++;
      }
      if (!videoRef.current) {
        setScanState('error');
        setErrMsg('Video element not ready. Please close and try again.');
        return;
      }

      try {
        const streamPromise = navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width:  { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Camera request timed out — please allow camera access in the browser address bar')), 10000)
        );
        const stream = await Promise.race([streamPromise, timeoutPromise]);

        if (!mountedRef.current) { stream.getTracks().forEach(t=>t.stop()); return; }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch {}
        if (!mountedRef.current) return;
        setScanState('running');
        startQuagga();
      } catch (e) {
        if (!mountedRef.current) return;
        const msg = e?.message || String(e);
        if (msg.includes('timed out') || facingMode !== 'environment') {
          setScanState('error');
          setErrMsg(msg.includes('timed out')
            ? 'Camera timed out. Click the camera icon in the address bar → Allow, then retry.'
            : msg);
        } else {
          tryStart('user');
        }
      }
    };

    tryStart('environment');
  }, [scanState, libReady]);

  const startQuagga = () => {
    if (!videoRef.current || runningRef.current) return;

    window.Quagga.init({
      inputStream: {
        type: 'LiveStream',
        target: videoRef.current,
        constraints: {},
        // Restrict scanning to a centered horizontal band — matches the
        // visual guide box drawn over the video. Barcodes outside this
        // region are not decoded, preventing accidental multi-barcode
        // misreads when several codes are visible in the same frame.
        area: { top: '30%', right: '10%', left: '10%', bottom: '30%' },
      },
      locator: { patchSize: 'medium', halfSample: true },
      numOfWorkers: navigator.hardwareConcurrency > 2 ? 2 : 1,
      frequency: 10,
      decoder: {
        readers: ['code_128_reader','ean_reader','ean_8_reader','code_39_reader'],
        multiple: false,
      },
      locate: true,
    }, (err) => {
      if (!mountedRef.current) return;
      if (err) { console.warn('Quagga init error (non-fatal):', err); return; }
      window.Quagga.start();
      runningRef.current = true;

      window.Quagga.onDetected((result) => {
        const code = result?.codeResult?.code;
        if (!code || !mountedRef.current) return;
        if (code === lastScanRef.current) return; // debounce same code
        lastScanRef.current = code;
        setTimeout(() => { lastScanRef.current = ''; }, 1200);
        if (navigator.vibrate) navigator.vibrate(60);
        setLastScan(code);
        setTimeout(() => { if (mountedRef.current) setLastScan(''); }, 1500);

        if (batchMode) {
          // Accumulate unique codes instead of firing immediately —
          // lets the operator hold the camera over a whole printed page
          // and collect every barcode before submitting them all at once.
          setBatchCodes(prev => prev.includes(code) ? prev : [...prev, code]);
        } else {
          onResult(code);
        }
      });
    });
  };

  const retry = () => { cleanup(); setScanState('starting'); setErrMsg(''); };

  const removeBatchCode = (code) => setBatchCodes(prev => prev.filter(c => c !== code));
  const submitBatch = () => {
    if (!batchCodes.length) return;
    onBatchSubmit?.(batchCodes);
    setBatchCodes([]);
  };

  return (
    <div className="cam-overlay" onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div className="cam-modal">
        <div className="cam-hdr">
          <span>📷 {batchMode ? 'Batch Scan — Whole Page' : 'Camera Scanner'}</span>
          <button className="cam-close" onClick={onClose}>✕</button>
        </div>

        {/* Our own <video> element — always in DOM so ref is always attached */}
        <div className="cam-region">
          <video ref={videoRef} className="cam-video"
            playsInline muted autoPlay
            style={{display: scanState==='running' ? 'block' : 'none'}}/>
          {scanState==='running' && (
            <>
              {/* Visual guide box — matches Quagga's area restriction exactly.
                  Only barcodes inside this box are decoded, preventing
                  accidental reads of neighboring barcodes on a printed sheet. */}
              <div className="cam-guide-box"/>
              <div className="cam-guide-hint">Place barcode inside the box</div>
            </>
          )}
          {(scanState==='loading'||scanState==='starting') && (
            <div className="cam-spinner">⟳ {scanState==='loading'?'Loading scanner…':'Starting camera…'}</div>
          )}
        </div>

        {scanState==='running' && !batchMode && (
          <div className="cam-status ok">● Live — align barcode inside the box</div>
        )}
        {scanState==='running' && batchMode && (
          <div className="cam-status ok">
            ● Live — {batchCodes.length} unique barcode{batchCodes.length!==1?'s':''} collected
          </div>
        )}
        {scanState==='error' && (
          <div className="cam-status err">
            ✕ {errMsg || 'Camera failed'}
            <div style={{marginTop:4,fontSize:10,opacity:.8}}>
              {errMsg?.includes?.('allocate')||errMsg?.includes?.('use')
                ? 'Another app is using the camera — close Zoom/FaceTime and retry'
                : 'Allow camera in browser settings, then retry'}
            </div>
            <button onClick={retry} style={{
              marginTop:10,padding:'6px 16px',borderRadius:6,border:'none',
              background:'#6366f1',color:'#fff',fontWeight:700,fontSize:12,cursor:'pointer'
            }}>↺ Retry</button>
          </div>
        )}
        {lastScan && !batchMode && (
          <div style={{background:'#052e16',color:'#4ade80',padding:'6px 12px',
            borderRadius:6,marginTop:6,fontFamily:'monospace',
            fontSize:13,fontWeight:700,textAlign:'center'}}>
            ✓ {lastScan}
          </div>
        )}

        {/* Batch mode — accumulated codes shown as removable chips */}
        {batchMode && (
          <div className="cam-batch-panel">
            {batchCodes.length===0 ? (
              <div style={{fontSize:11,color:'#ADB5BD',textAlign:'center',padding:'8px 0'}}>
                Move the camera slowly over the page — each new barcode
                found inside the box will be added below.
              </div>
            ) : (
              <div className="cam-batch-chips">
                {batchCodes.map(code=>(
                  <span key={code} className="cam-chip">
                    {code}
                    <button onClick={()=>removeBatchCode(code)}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <button className="btn btn-g" style={{width:'100%',marginTop:8}}
              disabled={!batchCodes.length}
              onClick={submitBatch}>
              ✓ Process {batchCodes.length} Barcode{batchCodes.length!==1?'s':''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const bridge = {
  getFactories: db => (db?.suppliers||[]).filter(s => s.supplierType==='Garment Supplier'),
  getLines: fac => fac?.sewingLines || fac?.lines || fac?.productionLines || fac?.sewLines || [],
  getAllLines: db => {
    const out = [];
    bridge.getFactories(db).forEach(f =>
      bridge.getLines(f).forEach(l =>
        out.push({ ...l, factoryName:f.name, factoryId:f.id })
      )
    );
    return out;
  },
  getStyleData: (styleId, db) => {
    const s = (db?.mgmtStyles||[]).find(st => st.id===styleId);
    if (!s) return null;
    const styleRef   = s.styleRef || s.name || s.id;
    const rawPos     = s.pos || s.purchaseOrders || s.orders || s.workstationData?.pos || [];
    const poTableRows= (db?.poSizeTable||[]).filter(r => r.style_ref===styleRef);
    const pos = rawPos.map(p => ({
      num: p.num||p.po_no||p.poNo||'-',
      qty: parseFloat(p.qty||p.quantity||p.total_qty||0)||0,
      col: p.col||p.color||p.colour||'',
      date:p.date||p.ex_fty_date||'',
      sizeBreakdown: p.sizeBreakdown||p.size_breakdown||'',
    })).filter(p => p.num!=='-');
    const colors = [...new Set([...pos.map(p=>p.col).filter(Boolean),...poTableRows.map(r=>r.color).filter(Boolean)])];
    const sizesFromBreakdown = pos.flatMap(p => {
      const sb = p.sizeBreakdown;
      if (typeof sb==='string'&&sb.trim()) return sb.split(',').map(s=>s.split(':')[0].trim()).filter(Boolean);
      if (sb&&typeof sb==='object') return Object.keys(sb);
      return [];
    });
    const sizes = [...new Set([...poTableRows.map(r=>r.size).filter(Boolean), ...sizesFromBreakdown])];
    const sizeColorMatrix = poTableRows.map(r => ({ poNo:r.po_no, color:r.color, size:r.size, qty:parseInt(r.qty)||0 }));
    const ops      = s.workstationData?.aggregatedOps || [];
    const totalSMV = parseFloat(s.workstationData?.smv) || ops.reduce((a,op)=>a+(parseFloat(op.smv)||0),0);
    return { id:s.id, styleRef, brand:s.brand||'', product:s.product||s.productType||'',
      fabric:s.fabric||'', buyer:s.buyer||'', season:s.season||'',
      colors, sizes, pos, totalQty: parseFloat(s.totalQty)||pos.reduce((a,p)=>a+(p.qty||0),0),
      sizeColorMatrix, ops, totalSMV, opCount:ops.length, _raw:s };
  },
  getPOSizeQty: (styleRef, poNo, db) => {
    const rows = (db?.poSizeTable||[]).filter(r => r.style_ref===styleRef && (!poNo||r.po_no===poNo));
    return rows.map(r => ({ color:r.color, size:r.size, qty:parseInt(r.qty)||0, poNo:r.po_no }));
  },
};

// ── helpers ──────────────────────────────────────────────────────────────────
const netMin = (shiftHrs, lunchMin=30, teaMin=10) => Math.max(0, shiftHrs*60 - lunchMin - teaMin);
const numPeriods = (workHrs, durHrs, breakMin=40) => Math.max(0, Math.floor((workHrs*60-breakMin)/(durHrs*60)));

// CSV export — Excel-openable, no library dependency. Used for garment
// unit exports since these can run into thousands of rows (one per piece).
function downloadCSV(filename, rows, columns) {
  const header = columns.map(c=>`"${c.label}"`).join(',');
  const body = rows.map(r =>
    columns.map(c => `"${String(r[c.key] ?? '').replace(/"/g,'""')}"`).join(',')
  ).join('\n');
  const blob = new Blob([header+'\n'+body], { type:'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Alert({ msg, type='inf', onClose }) {
  if (!msg) return null;
  return (
    <div className={`alert ${type}`}>
      {msg}
      {onClose && <button onClick={onClose} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',fontSize:14}}>✕</button>}
    </div>
  );
}

function SrcBadge({ src }) {
  const map = {
    NIRVIKALP: { bg:'#eff6ff', color:'#0D47A1', border:'#90CAF9', text:'From Nirvikalp' },
    LOCAL:     { bg:'#FFF8E1', color:'#b45309', border:'#fcd34d', text:'New Entry' },
  };
  const s = map[src] || map.LOCAL;
  return <span className="src-badge" style={{background:s.bg,color:s.color,borderColor:s.border}}>{s.text}</span>;
}

function Stats({ items }) {
  return (
    <div className="stats">
      {items.map(s => (
        <div key={s.label} className="stat-b">
          <div className="stat-l">{s.label}</div>
          <div className="stat-v" style={s.color?{color:s.color}:{}}>{s.val??'—'}</div>
          {s.sub && <div className="stat-s">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ── Ticket visual — renders either Code128 barcode or Qaram-compatible QR,
// depending on the app-wide format toggle. `qrPayload` should already be
// built via the QR.* helpers so it's Qaram-parseable. ─────────────────────
function TicketVisual({ format, value, qrPayload, height=38, barWidth=1.4, qrSize=100 }) {
  if (format === 'qr') {
    const data = qrPayload || value;
    return (
      <img src={qrUrl(data, qrSize)} alt="QR" width={qrSize} height={qrSize}
        style={{ display:'block', background:'#fff', borderRadius:4 }}
        onError={e=>{ e.target.style.display='none'; }}/>
    );
  }
  return <Barcode value={value} height={height} fontSize={9} width={barWidth}/>;
}

// ── Ticket card — like BarcodeCard but format-aware and Qaram-interoperable ─
function TicketCard({ label, subLabel, value, qrPayload, format }) {
  return (
    <div style={{
      background:'#fff', border:'2px solid #DEE2E6', borderRadius:8,
      padding:'10px 12px', display:'flex', flexDirection:'column',
      alignItems:'center', gap:5, minWidth:140, width:140, userSelect:'none',
    }}>
      {label && (
        <div style={{fontSize:10,fontWeight:700,color:'#6C757D',textTransform:'uppercase',
          letterSpacing:'.5px',textAlign:'center',lineHeight:1.2}}>{label}</div>
      )}
      <TicketVisual format={format} value={value} qrPayload={qrPayload} qrSize={92}/>
      <div style={{fontFamily:'monospace',fontSize:9,color:'#2C3E50',fontWeight:600,
        textAlign:'center',wordBreak:'break-all'}}>
        {format==='qr' ? (qrPayload||value) : value}
      </div>
      {subLabel && <div style={{fontSize:9,color:'#ADB5BD',textAlign:'center'}}>{subLabel}</div>}
    </div>
  );
}

// ── Efficiency ring ──────────────────────────────────────────────────────────
function EffRing({ value, size=64 }) {
  const v   = Math.min(150, Math.max(0, parseFloat(value)||0));
  const r   = (size/2) - 5;
  const circ= 2*Math.PI*r;
  const dash= Math.min(1, v/100) * circ;
  const col = v>=90?'#388E3C':v>=70?'#E65100':'#C62828';
  const cx  = size/2, cy = size/2;
  return (
    <svg width={size} height={size} style={{display:'block'}}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#DEE2E6" strokeWidth={5}/>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth={5}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}/>
      <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle"
        fontSize={size<56?10:13} fontWeight={700} fill={col}>{v.toFixed(0)}%</text>
    </svg>
  );
}

// ── Print ticket for one bundle ───────────────────────────────────────────────
// Operations split into rows of 4 max — each barcode gets ~21mm at 92mm ticket
// width, making them reliably scannable on inkjet. 2 tickets still fit per A4 row.
function BundleTicket({ bundle, ops, styleRef, poNo, styleQty, orderQty, deliveryDate, country }) {
  const chunkSize = 4;
  const opRows = [];
  for (let i = 0; i < ops.length; i += chunkSize) opRows.push(ops.slice(i, i + chunkSize));
  return (
    <div className="bundle-ticket">
      <div className="ticket-badge">{bundle.bundle_no}</div>

      {opRows.map((row, ri) => (
        <div key={ri} className="ticket-ops-row">
          {row.map((op,i)=>(
            <div key={i} className="ticket-op-cell">
              <div className="ticket-op-abbr">{op.op_name?.slice(0,16)||op.op_code}</div>
              <div className="ticket-op-barcode">
                <Barcode value={op.barcode||'000000'} height={48} width={1.6}
                  fontSize={0} displayValue={false}/>
              </div>
              <div className="ticket-op-num">{op.barcode||''}</div>
              <div className="ticket-op-meta">
                <span>{ri*chunkSize+i+1}</span>
                <span>{op.part_name?.slice(0,10)||'—'}</span>
                <span>SMV {parseFloat(op.smv||0).toFixed(3)}</span>
              </div>
            </div>
          ))}
        </div>
      ))}

      <table className="ticket-info">
        <tbody>
          <tr>
            <td className="ti-lbl">Style</td><td className="ti-val ti-style">{styleRef}</td>
            <td className="ti-lbl">Order</td><td className="ti-val ti-order">{poNo||'—'}</td>
            <td className="ti-lbl">Lay</td><td className="ti-val ti-num">{bundle.lay_no}</td>
          </tr>
          <tr>
            <td className="ti-lbl">Sty Qty</td><td className="ti-val ti-num">{(styleQty||0).toLocaleString()}</td>
            <td className="ti-lbl">Ord Qty</td><td className="ti-val ti-num">{(orderQty||0).toLocaleString()}</td>
            <td className="ti-lbl">Bnd Qty</td><td className="ti-val ti-num">{bundle.qty}</td>
          </tr>
          <tr>
            <td className="ti-lbl">Size</td><td className="ti-val ti-num">{bundle.size||'—'}</td>
            <td className="ti-lbl">Color</td><td className="ti-val">{bundle.color||'—'}</td>
            <td className="ti-lbl">Ply</td><td className="ti-val ti-num">{bundle.ply_from||'—'}-{bundle.ply_to||'—'}</td>
          </tr>
          <tr>
            <td className="ti-lbl">Part</td><td className="ti-val">{ops[0]?.part_name||'—'}</td>
            <td className="ti-lbl">Delivery</td><td className="ti-val">{deliveryDate||'—'}</td>
            <td className="ti-lbl">Country</td><td className="ti-val">{country||'—'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Live scan row in log ──────────────────────────────────────────────────────
function ScanRow({ entry }) {
  const col = entry.ok ? '#15803d' : entry.type==='DUPLICATE' ? '#b45309' : '#C62828';
  const bg  = entry.ok ? '#f0fdf4' : entry.type==='DUPLICATE' ? '#FFF8E1' : '#FFEBEE';
  const border = entry.ok ? '#86efac' : entry.type==='DUPLICATE' ? '#fcd34d' : '#fca5a5';

  return (
    <div style={{borderRadius:6,background:bg,border:`1px solid ${border}`,
      fontSize:11,marginBottom:4, padding:'5px 10px'}}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontWeight:700,color:col,minWidth:80}}>{entry.type}</span>
        <span style={{fontFamily:'monospace',flex:1,color:'#2C3E50',wordBreak:'break-all'}}>{entry.val}</span>
        {entry.ok && entry.data?.smv_minutes>0 &&
          <span style={{color:'#6A1B9A',fontWeight:700,whiteSpace:'nowrap'}}>
            SMV {entry.data.smv_minutes.toFixed(3)}
          </span>
        }
        {!entry.ok && entry.type!=='DUPLICATE' && <span style={{color:col}}>{entry.error}</span>}
        {entry.type==='DUPLICATE' && <span style={{color:col,fontWeight:700}}>⚠ Already scanned</span>}
        <span style={{color:'#ADB5BD',fontSize:10,whiteSpace:'nowrap'}}>
          {new Date(entry.ts).toLocaleTimeString()}
        </span>
      </div>
      {entry.type==='DUPLICATE' && entry.duplicateDetail && (
        <div style={{marginTop:5,paddingTop:5,borderTop:'1px dashed #fcd34d',
          display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'2px 12px',fontSize:10,color:'#92400e'}}>
          <span>Operator: <b>{entry.duplicateDetail.operator_name||'—'}</b></span>
          <span>Process: <b>{entry.duplicateDetail.op_name||'—'}</b></span>
          <span>Date: <b>{entry.duplicateDetail.date||'—'}</b></span>
          <span>Period: <b>{entry.duplicateDetail.period_no||'—'}</b></span>
          <span>Style/PO: <b>{entry.duplicateDetail.style_ref||'—'} / {entry.duplicateDetail.order_no||'—'}</b></span>
          <span>Lay/Bundle: <b>L{entry.duplicateDetail.lay_no||'—'} · B{entry.duplicateDetail.bundle_no||'—'}</b></span>
          <span>Size: <b>{entry.duplicateDetail.size||'—'}</b></span>
          <span>Time: <b>{entry.duplicateDetail.scanned_at ? new Date(entry.duplicateDetail.scanned_at).toLocaleTimeString() : '—'}</b></span>
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const TABS = [
  { key:'plan',    label:'📋 Cut Plan'  },
  { key:'lays',    label:'📐 Lays'      },
  { key:'barcodes',label:'🔷 Barcodes'  },
  { key:'print',   label:'🖨 Print'     },
  { key:'garment', label:'🏷 Garment QR'},
  { key:'scan',    label:'📷 Scan'      },
  { key:'setup',   label:'⚙️ Setup'     },
];

export default function ScanModule({ db, pmDb, setPmDb, apiLive }) {
  const [tab,    setTab]  = useState('plan');
  const [alert,  setAlert]= useState(null);
  const [saving, setSaving]= useState(false);

  // ── style / PO selection (shared across Plan + Lays) ───────────────────────
  const [selStyleId, setSelStyle] = useState('');
  const [selPO,      setSelPO]    = useState('');

  const styleData     = selStyleId ? bridge.getStyleData(selStyleId, db) : null;
  const sizeColorRows = styleData  ? bridge.getPOSizeQty(styleData.styleRef, selPO, db) : [];
  const plans         = (pmDb.cutPlans||[]).filter(p => p.style_id===selStyleId);

  // ── lay form ───────────────────────────────────────────────────────────────
  const [layForm, setLayForm] = useState({
    layNo:1, plyFrom:1, plyTo:1, cutQty:0, bundleQty:12, size:'', color:''
  });

  // ── period / scan state ────────────────────────────────────────────────────
  const [pf, setPF] = useState({
    date: new Date().toISOString().slice(0,10), workHrs:10, durHrs:2, lineId:''
  });
  // Lines filtered to style's allocated factories when a style is selected
  const allLines = useMemo(() => {
    const allFactoryLines = bridge.getAllLines(db);
    if (!styleData) return allFactoryLines;
    // Get factoryIds allocated to this style
    const allocatedIds = (styleData._raw?.workstationData?.allocatedLines ||
                          styleData._raw?.allocatedLines || [])
      .map(a => String(a.factoryId || a.factory_id || a.supplierId || ''))
      .filter(Boolean);
    if (!allocatedIds.length) return allFactoryLines;
    // Filter to only lines from allocated factories
    const filtered = allFactoryLines.filter(l =>
      allocatedIds.includes(String(l.factoryId))
    );
    return filtered.length ? filtered : allFactoryLines;
  }, [db, styleData]);
  const nm        = netMin(pf.workHrs);
  const np        = numPeriods(pf.workHrs, pf.durHrs);
  const todayPers = (pmDb.periods||[]).filter(p=>p.date===pf.date&&p.line_id===pf.lineId);

  // ── scan state ─────────────────────────────────────────────────────────────
  const [scanStep, setScanStep]   = useState('period');
  const [ctx, setCtx]             = useState({ period:null, line:null, operator:null });
  const [scanLog,  setScanLog]    = useState([]);
  const [manualBC, setManualBC]   = useState('');
  const [sessionStats, setStats]  = useState({ scans:0, smvEarned:0, pieces:0 });
  const scanRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showBatchCamera, setShowBatchCamera] = useState(false);

  // ── Ticket format toggle — Barcode (Code128) or QR (Qaram-compatible) ─────
  // Applies to all tickets we generate: lines, periods, operators, bundle/op
  // tickets, lost time reasons. Barcode is the reliable default for handheld
  // scanner guns; QR enables interop with the Qaram platform's own scanner.
  const [ticketFormat, setTicketFormat] = useState('barcode'); // 'barcode' | 'qr'

  // ── Employee barcode print filters — group by factory/dept/line, or pick individually ──
  const [empGroupBy, setEmpGroupBy] = useState('all'); // all|factory|department|line|individual
  const [empFilterVal, setEmpFilterVal] = useState('');
  const [empSelected, setEmpSelected] = useState(new Set());

  const empFilterOptions = useMemo(() => {
    const workers = pmDb.workers || [];
    const key = empGroupBy==='factory' ? 'factory_name'
              : empGroupBy==='department' ? 'department'
              : empGroupBy==='line' ? 'line_name'
              : null;
    if (!key) return [];
    const vals = new Set();
    workers.forEach(w => { const v = w[key]||w[key.replace('_name','_id')]; if (v) vals.add(v); });
    return [...vals].sort();
  }, [pmDb.workers, empGroupBy]);

  const filteredWorkers = useMemo(() => {
    const workers = pmDb.workers || [];
    if (empGroupBy==='all') return workers;
    if (empGroupBy==='individual') return workers.filter(w => empSelected.has(w.id));
    const key = empGroupBy==='factory' ? 'factory_name'
              : empGroupBy==='department' ? 'department'
              : 'line_name';
    if (!empFilterVal) return [];
    return workers.filter(w => (w[key]||w[key.replace('_name','_id')]) === empFilterVal);
  }, [pmDb.workers, empGroupBy, empFilterVal, empSelected]);

  // ── Lost time reasons — editable, persisted, falls back to defaults ───────
  const lostTimeReasons = (pmDb.lostTimeReasons && pmDb.lostTimeReasons.length)
    ? pmDb.lostTimeReasons : DEFAULT_LOST_TIME_REASONS;

  const saveLostTimeReasons = async (reasons) => {
    const upd = {...pmDb, lostTimeReasons:reasons};
    setPmDb(upd); LS.set('lostTimeReasons', reasons);
    if (apiLive) await pmApi.save('pm_lost_time_reasons', reasons);
  };

  const addLostTimeReason = () => {
    const nextNum = lostTimeReasons.length + 1;
    const newReason = { code:`L${nextNum}`, label:'New Reason' };
    saveLostTimeReasons([...lostTimeReasons, newReason]);
  };
  const updateLostTimeReason = (code, label) => {
    saveLostTimeReasons(lostTimeReasons.map(r => r.code===code ? {...r,label} : r));
  };
  const deleteLostTimeReason = (code) => {
    saveLostTimeReasons(lostTimeReasons.filter(r => r.code!==code));
  };

  // ── scan mode: production vs lost time entry ──────────────────────────────
  const [scanMode, setScanMode] = useState('production'); // 'production' | 'lostTime' | 'garmentTrack'
  const [gtCheckpoint, setGtCheckpoint] = useState('LINE_IN'); // 'LINE_IN' | 'FINISHING_OUT'
  const [ltStep,   setLtStep]   = useState('worker');     // 'worker' | 'reason' | 'duration'
  const [ltWorker, setLtWorker] = useState(null);
  const [ltReason, setLtReason] = useState(null);
  const [ltDuration, setLtDuration] = useState(30);
  // Batch mode — for whole-line events (power failure, no material) where
  // scanning every worker individually is impractical. Supervisor sweep-
  // scans everyone affected, picks one reason+duration, submits once —
  // creates one accurate record per worker so cost-impact math stays real
  // instead of guessing a headcount multiplier.
  const [ltBatchMode, setLtBatchMode] = useState(false);
  const [ltBatchWorkers, setLtBatchWorkers] = useState([]); // [{operator_id, name}]
  const [logView, setLogView]   = useState('log'); // 'log' | 'invalid'

  // ── invalid scan log — persisted, auto-purges entries older than 7 days ────
  useEffect(() => {
    const sevenDaysAgo = Date.now() - 7*24*60*60*1000;
    const existing = pmDb.invalidScans || [];
    const cleaned = existing.filter(s => s.ts > sevenDaysAgo);
    if (cleaned.length !== existing.length) {
      const upd = {...pmDb, invalidScans:cleaned};
      setPmDb(upd); LS.set('invalidScans', cleaned);
    }
  }, []); // run once on mount

  const logInvalidScan = useCallback((val, reason, source) => {
    const rec = { val, reason, source, ts:Date.now(), date:new Date().toISOString().slice(0,10) };
    const upd = {...pmDb, invalidScans:[rec, ...(pmDb.invalidScans||[])].slice(0,500)};
    setPmDb(upd); LS.set('invalidScans', upd.invalidScans);
  }, [pmDb, setPmDb]);

  // auto-focus scan input when on scan tab
  useEffect(() => {
    if (tab==='scan') setTimeout(()=>scanRef.current?.focus(),100);
  }, [tab]);

  // ── derived scan metrics ───────────────────────────────────────────────────
  const lineConfig = ctx.line ? (pmDb.lineConfigs||[]).find(l=>l.barcode===ctx.line?.barcode) : null;
  const availMin   = useMemo(()=>{
    const at = (pmDb.availableTimes||[]).find(a=>a.lineId===ctx.line?.line_id||a.line_id===ctx.line?.line_id);
    return parseInt(at?.net_minutes||at?.netMinutes||480);
  },[pmDb.availableTimes, ctx.line]);
  const workers    = useMemo(()=>{
    const wks = (pmDb.workers||[]).filter(w=>w.line_id===ctx.line?.line_id);
    return wks.length || parseInt(lineConfig?.workers_count||lineConfig?.workers||20);
  },[pmDb.workers, ctx.line, lineConfig]);

  // ── Lost time for current operator (today) — reduces THEIR available time
  // so their efficiency stays fair despite factors outside their control ────
  const operatorLostMin = useMemo(() => {
    if (!ctx.operator) return 0;
    const today = new Date().toISOString().slice(0,10);
    return (pmDb.lostTimeRecords||[])
      .filter(r => r.operator_id===ctx.operator.operator_id && r.date===today)
      .reduce((s,r) => s + (parseInt(r.duration_min)||0), 0);
  }, [pmDb.lostTimeRecords, ctx.operator]);

  // Section-wide lost time (today, this line) — does NOT reduce section's
  // available minutes, so it naturally drags section efficiency down,
  // surfacing where resources are being wasted without blaming workers.
  const sectionLostMin = useMemo(() => {
    if (!ctx.line) return 0;
    const today = new Date().toISOString().slice(0,10);
    return (pmDb.lostTimeRecords||[])
      .filter(r => r.line_id===ctx.line.line_id && r.date===today)
      .reduce((s,r) => s + (parseInt(r.duration_min)||0), 0);
  }, [pmDb.lostTimeRecords, ctx.line]);

  // Lost time breakdown by reason — for the waste-intelligence panel
  const lostTimeByReason = useMemo(() => {
    const today = new Date().toISOString().slice(0,10);
    const relevant = (pmDb.lostTimeRecords||[]).filter(r =>
      r.date===today && (!ctx.line || r.line_id===ctx.line.line_id)
    );
    const byReason = {};
    relevant.forEach(r => {
      byReason[r.reason_code] = (byReason[r.reason_code]||0) + (parseInt(r.duration_min)||0);
    });
    return lostTimeReasons
      .map(rs => ({ ...rs, minutes: byReason[rs.code]||0 }))
      .filter(rs => rs.minutes > 0)
      .sort((a,b) => b.minutes - a.minutes);
  }, [pmDb.lostTimeRecords, ctx.line, lostTimeReasons]);
  const operatorAvailMin = Math.max(1, availMin - operatorLostMin);
  const operatorEff = useMemo(() => {
    if (!sessionStats.smvEarned) return 0;
    return Math.min(200, Math.round(sessionStats.smvEarned/operatorAvailMin*100));
  }, [sessionStats.smvEarned, operatorAvailMin]);

  // Section efficiency — RAW: available time NOT reduced by lost time,
  // so high lost time = lower section efficiency = visible resource waste
  const sectionAvailMin = availMin * workers;
  const sectionEff = useMemo(() => {
    if (!sessionStats.smvEarned || !sectionAvailMin) return 0;
    return Math.min(200, Math.round(sessionStats.smvEarned/sectionAvailMin*100));
  }, [sessionStats.smvEarned, sectionAvailMin]);
  const sectionLostPct = sectionAvailMin ? Math.round(sectionLostMin/sectionAvailMin*100) : 0;

  // liveEff kept as alias to section efficiency for backward compat with existing UI
  const liveEff = sectionEff;

  // ── plan actions (unchanged from original) ────────────────────────────────
  const createPlan = async () => {
    if (!selStyleId||!styleData) return setAlert({msg:'Select a style first',type:'err'});
    setSaving(true);
    const plan = {
      id:Date.now(), style_id:selStyleId, style_ref:styleData.styleRef,
      style_name:styleData.styleRef, order_no:selPO,
      total_smv:styleData.totalSMV, ops_count:styleData.opCount,
      source_flag:'LOCAL', created_at:new Date().toISOString(),
    };
    const upd = {...pmDb, cutPlans:[...(pmDb.cutPlans||[]),plan]};
    setPmDb(upd); LS.set('cutPlans',upd.cutPlans);
    if (apiLive) await pmApi.save('pm_cut_plan',plan);
    setSaving(false);
    setAlert({msg:`Cut plan created for ${styleData.styleRef}`,type:'ok'});
  };

  const addLay = async (planId) => {
    if (!layForm.size) return setAlert({msg:'Select a size',type:'err'});
    setSaving(true);
    const ops = styleData?.ops||[];
    const bc  = Math.ceil(layForm.cutQty/layForm.bundleQty)||1;
    const bundles=[], barcodes=[];
    let plyCursor = layForm.plyFrom;
    for (let b=1; b<=bc; b++) {
      const bQty = b===bc?((layForm.cutQty%layForm.bundleQty)||layForm.bundleQty):layForm.bundleQty;
      const bndId= `${planId}-L${layForm.layNo}-B${b}`;
      const plyStart = plyCursor;
      const plyEnd   = plyCursor + bQty - 1;
      plyCursor = plyEnd + 1;
      bundles.push({ id:bndId, plan_id:planId, lay_no:layForm.layNo, bundle_no:b,
        size:layForm.size, color:layForm.color, qty:bQty,
        ply_from:plyStart, ply_to:plyEnd,
        style_desc:styleData?.product||styleData?._raw?.description||'',
        barcode:BC.bundle(planId,layForm.layNo,b), source_flag:'LOCAL' });
      ops.forEach((op,oi) => barcodes.push({
        bundle_id:bndId, bundle_no:b, lay_no:layForm.layNo, plan_id:planId,
        op_code:op.procName||`OP${oi+1}`, op_name:op.procName, part_name:op.partName,
        machine:op.machine, smv:op.smv,
        barcode:BC.opBundle(planId,layForm.layNo,b,oi),
        barcode_type:'OPERATION',
      }));
      // Garment unit — one permanent QR per physical piece. Carries only a
      // bare numeric barcode (no descriptive text) for maximum wash/heat-
      // transfer durability; all context is denormalized here for exports
      // and reporting, looked up by that one number rather than encoded
      // inside the QR itself.
      Array.from({length:bQty},(_,gi)=>barcodes.push({
        bundle_id:bndId, bundle_no:b, lay_no:layForm.layNo, piece_no:gi+1,
        barcode:BC.garmentUnit(planId,layForm.layNo,b,gi+1),
        barcode_type:'GARMENT_UNIT', plan_id:planId,
        style_ref:styleData?.styleRef, order_no:selPO,
        size:layForm.size, color:layForm.color,
        created_at:new Date().toISOString(),
      }));
    }
    const lay = { id:`${planId}-L${layForm.layNo}`, plan_id:planId, ...layForm,
      bundle_count:bc, source_flag:'LOCAL' };
    const upd = {...pmDb,
      lays:[...(pmDb.lays||[]),lay],
      bundles:[...(pmDb.bundles||[]),...bundles],
      barcodes:[...(pmDb.barcodes||[]),...barcodes],
    };
    setPmDb(upd);
    LS.set('lays',upd.lays); LS.set('bundles',upd.bundles); LS.set('barcodes',upd.barcodes);
    if (apiLive) {
      await pmApi.save('pm_lay',lay);
      await pmApi.batchSave('pm_bundle',bundles);
      await pmApi.batchSave('pm_barcode',barcodes.filter(b=>b.barcode_type==='OPERATION').map(b=>({
        barcode:b.barcode, barcode_type:b.barcode_type,
        style:styleData.styleRef, po_no:selPO, color:layForm.color, size:layForm.size,
        plan_id:planId, lay_no:layForm.layNo, bundle_no:b.bundle_no,
        bundle_qty:bQty, part_name:b.part_name, process_name:b.op_name,
        smv:b.smv, machine_type:b.machine,
      })));

      // Garment units were previously never synced — only OPERATION type
      // barcodes were pushed above. Fixed: push GARMENT_UNIT records too,
      // so a different device scanning this unit's checkpoint later can
      // actually find it instead of returning "Unknown garment QR".
      const garmentUnits = barcodes.filter(b=>b.barcode_type==='GARMENT_UNIT');
      if (garmentUnits.length) {
        await pmApi.batchCall('save_garment_units', garmentUnits.map(u=>({
          barcode:u.barcode, plan_id:u.plan_id, lay_no:u.lay_no,
          bundle_no:u.bundle_no, piece_no:u.piece_no,
          style_ref:u.style_ref, order_no:u.order_no,
          size:u.size, color:u.color,
        })));
      }
    }
    setLayForm(f=>({...f,layNo:f.layNo+1}));
    setSaving(false);
    setAlert({msg:`Lay ${layForm.layNo}: ${bc} bundles · ${barcodes.length} barcodes generated`,type:'ok'});
  };

  // ── period generation ──────────────────────────────────────────────────────
  const genPeriods = async () => {
    if (!pf.lineId) return setAlert({msg:'Select a line',type:'err'});
    setSaving(true);
    const perMin = Math.floor(pf.durHrs*60);
    const periods = Array.from({length:np},(_,i)=>({
      id:`${pf.date}-L${pf.lineId}-P${i+1}`,
      date:pf.date, line_id:pf.lineId, period_no:i+1,
      start_min:i*perMin, end_min:(i+1)*perMin, duration_min:perMin,
      barcode:BC.period(pf.date,pf.lineId,i+1), source_flag:'LOCAL',
    }));
    const upd = {...pmDb, periods:[...(pmDb.periods||[]).filter(p=>!(p.date===pf.date&&p.line_id===pf.lineId)), ...periods]};
    setPmDb(upd); LS.set('periods',upd.periods);
    if (apiLive) await pmApi.batchSave('pm_period',periods);
    setSaving(false);
    setAlert({msg:`${np} periods generated for ${pf.date}`,type:'ok'});
  };

  // ── scan processing ────────────────────────────────────────────────────────
  // source: 'manual' (text input/gun) | 'camera' (camera scanner)
  const processScan = useCallback(async (raw, source='manual') => {
    const v = raw.trim(); if (!v) return;
    setManualBC('');

    // Normalize common Code128 charset-switching misreads
    const candidates = [v, v.toUpperCase()];
    const findMatch = (arr, key) =>
      arr?.find(item => candidates.some(c => item[key] === c));

    const period = findMatch(pmDb.periods||[], 'barcode');
    const lineCfg= findMatch(pmDb.lineConfigs||[], 'barcode');
    const worker = findMatch(pmDb.workers||[], 'worker_barcode');
    const bundle = findMatch(pmDb.bundles||[], 'barcode');
    const opBC   = (pmDb.barcodes||[]).find(b=>candidates.includes(b.barcode)&&b.barcode_type==='OPERATION');

    let entry = { type:'UNKNOWN', val:v, ok:false, error:`Expected: ${scanStep}`, ts:Date.now(), source };
    let isInvalid = !period && !lineCfg && !worker && !bundle && !opBC;

    if (period && scanStep==='period') {
      setCtx(c=>({...c,period})); setScanStep('line');
      entry = { type:'PERIOD', val:v, ok:true, data:period,
        info:`Period ${period.period_no} · ${period.duration_min} min`, ts:Date.now(), source };

    } else if (lineCfg && scanStep==='line') {
      setCtx(c=>({...c,line:lineCfg})); setScanStep('operator');
      entry = { type:'LINE', val:v, ok:true, data:lineCfg,
        info:`${lineCfg.line_name} · ${lineCfg.factory_name}`, ts:Date.now(), source };

    } else if (worker && scanStep==='operator') {
      setCtx(c=>({...c,operator:worker})); setScanStep('bundle');
      entry = { type:'OPERATOR', val:v, ok:true, data:worker,
        info:`${worker.name} · ${worker.designation}`, ts:Date.now(), source };

    } else if ((bundle||opBC) && scanStep==='bundle') {

      // ── DUPLICATE CHECK — one barcode can only be scanned once ──
      const prior = (pmDb.scanRecords||[]).find(r => r.barcode===v);
      if (prior) {
        entry = {
          type:'DUPLICATE', val:v, ok:false, source, ts:Date.now(),
          error: 'Already scanned — barcode cannot be reused',
          duplicateDetail: prior,
        };
        setScanLog(l=>[entry,...l].slice(0,200));
        setTimeout(()=>scanRef.current?.focus(),50);
        return;
      }

      const smvMin  = parseFloat(opBC?.smv||0);
      const bndObj  = bundle || (pmDb.bundles||[]).find(b=>b.id===opBC?.bundle_id);
      const pieces  = bndObj?.qty || 0;

      // Denormalize style/PO for duplicate-detail display later
      const plan = (pmDb.cutPlans||[]).find(p=>p.id===bndObj?.plan_id);

      const rec = {
        id:Date.now(),
        period_id:ctx.period?.id,       period_no:ctx.period?.period_no,
        line_id:ctx.line?.line_id,      factory_id:ctx.line?.factory_id,
        line_name:ctx.line?.line_name,
        operator_id:ctx.operator?.operator_id, operator_name:ctx.operator?.name,
        bundle_id:bndObj?.id,           bundle_no:bndObj?.bundle_no,
        lay_no:bndObj?.lay_no,
        op_code:opBC?.op_code,          op_name:opBC?.op_name,
        part_name:opBC?.part_name,      machine:opBC?.machine,
        smv_minutes:smvMin,             pieces_counted:pieces,
        smv_earned:smvMin*pieces,
        size:bndObj?.size,              color:bndObj?.color,
        style_ref:plan?.style_ref,      order_no:plan?.order_no,
        barcode:v,
        scanned_at:new Date().toISOString(),
        date:new Date().toISOString().slice(0,10),
        source:'LIVE',
      };

      const upd = {...pmDb, scanRecords:[...(pmDb.scanRecords||[]),rec]};
      setPmDb(upd); LS.set('scanRecords',upd.scanRecords);

      setStats(s=>({
        scans:    s.scans+1,
        smvEarned:s.smvEarned + rec.smv_earned,
        pieces:   s.pieces + pieces,
      }));

      if (apiLive) {
        pmApi.save('pm_scan_record', rec);
        pmApi.processScan({
          barcode:v,
          context:{
            operator_id:ctx.operator?.operator_id,
            operator_name:ctx.operator?.name,
            line_id:ctx.line?.line_id,
            factory_id:ctx.line?.factory_id,
            period_id:ctx.period?.id,
            period_start:ctx.period?.start_min,
            period_end:ctx.period?.end_min,
            session_id:`${ctx.line?.line_id}-${ctx.period?.id}`,
          },
          source:'SCAN',
        });
      }

      entry = { type:opBC?'OPERATION':'BUNDLE', val:v, ok:true, data:rec,
        info:`${opBC?.op_name||'Bundle'} · ${pieces} pcs · SMV ${smvMin.toFixed(3)}`,
        ts:Date.now(), source };
    }

    // Invalid scan: log to persisted 7-day invalid log
    if (isInvalid) {
      logInvalidScan(v, `Expected ${scanStep} barcode`, source);
    }

    const entryId = entry.ts + Math.random();
    entry.id = entryId;
    setScanLog(l=>[entry,...l].slice(0,200));

    // Camera-sourced unknown scans: flash briefly then auto-remove from log
    // so continuous camera scanning doesn't spam the visible list
    if (source==='camera' && !entry.ok) {
      setTimeout(() => {
        setScanLog(l => l.filter(e => e.id !== entryId));
      }, 1500);
    }

    setTimeout(()=>scanRef.current?.focus(),50);
  },[scanStep,ctx,pmDb,apiLive,setPmDb,logInvalidScan]);

  // ── lost time entry processing — separate flow, supervisor-operated ───────
  const processLostTimeScan = useCallback((raw) => {
    const v = raw.trim(); if (!v) return;
    setManualBC('');
    const candidates = [v, v.toUpperCase()];

    if (ltStep==='worker') {
      const worker = (pmDb.workers||[]).find(w=>candidates.includes(w.worker_barcode));
      if (!worker) {
        logInvalidScan(v, 'Expected worker barcode (lost time entry)', 'manual');
        setScanLog(l=>[{type:'UNKNOWN',val:v,ok:false,error:'Expected worker barcode',ts:Date.now(),id:Date.now()},...l].slice(0,200));
        return;
      }
      setLtWorker(worker); setLtStep('reason');
      setScanLog(l=>[{type:'LT_WORKER',val:v,ok:true,info:`${worker.name} selected for lost time entry`,ts:Date.now(),id:Date.now()},...l].slice(0,200));

    } else if (ltStep==='reason') {
      const reasonMatch = lostTimeReasons.find(r => candidates.includes(BC.lostTime(r.code)));
      if (!reasonMatch) {
        logInvalidScan(v, 'Expected lost-time reason ticket', 'manual');
        setScanLog(l=>[{type:'UNKNOWN',val:v,ok:false,error:'Expected reason ticket',ts:Date.now(),id:Date.now()},...l].slice(0,200));
        return;
      }
      setLtReason(reasonMatch); setLtStep('duration');
      setScanLog(l=>[{type:'LT_REASON',val:v,ok:true,info:`${reasonMatch.label} selected`,ts:Date.now(),id:Date.now()},...l].slice(0,200));
    }
    setTimeout(()=>scanRef.current?.focus(),50);
  },[ltStep,pmDb.workers,logInvalidScan]);

  const submitLostTime = async () => {
    if (!ltWorker || !ltReason || !ltDuration) return;
    const rec = {
      id:Date.now(),
      operator_id:ltWorker.operator_id, operator_name:ltWorker.name,
      line_id:ctx.line?.line_id||ltWorker.line_id, line_name:ctx.line?.line_name||'',
      factory_id:ctx.line?.factory_id||'',
      reason_code:ltReason.code, reason_label:ltReason.label,
      duration_min:parseInt(ltDuration)||0,
      period_id:ctx.period?.id, period_no:ctx.period?.period_no,
      date:new Date().toISOString().slice(0,10),
      logged_at:new Date().toISOString(),
      logged_by:'Supervisor',
    };
    const upd = {...pmDb, lostTimeRecords:[...(pmDb.lostTimeRecords||[]),rec]};
    setPmDb(upd); LS.set('lostTimeRecords',upd.lostTimeRecords);
    if (apiLive) await pmApi.save('pm_lost_time', rec);

    setAlert({msg:`Lost time logged: ${ltReason.label} · ${ltDuration}min · ${ltWorker.name}`,type:'ok'});
    setLtWorker(null); setLtReason(null); setLtDuration(30); setLtStep('worker');
    setTimeout(()=>scanRef.current?.focus(),100);
  };

  // ── Batch lost time — sweep-scan every worker affected by a whole-line
  // event (power failure, no material) so cost impact reflects the real
  // headcount instead of one record standing in for the whole line.
  const addBatchWorker = (raw) => {
    const v = raw.trim(); if (!v) return;
    const candidates = [v, v.toUpperCase()];
    const worker = (pmDb.workers||[]).find(w=>candidates.includes(w.worker_barcode));
    if (!worker) {
      logInvalidScan(v, 'Expected worker barcode (batch lost time)', 'manual');
      return false;
    }
    setLtBatchWorkers(prev => prev.some(w=>w.operator_id===worker.operator_id)
      ? prev : [...prev, worker]);
    return true;
  };

  const submitBatchLostTime = async () => {
    if (!ltBatchWorkers.length || !ltReason || !ltDuration) return;
    const nowIso = new Date().toISOString();
    const today = nowIso.slice(0,10);
    const recs = ltBatchWorkers.map((w,i) => ({
      id: Date.now()+i,
      operator_id:w.operator_id, operator_name:w.name,
      line_id:ctx.line?.line_id||w.line_id, line_name:ctx.line?.line_name||'',
      factory_id:ctx.line?.factory_id||'',
      reason_code:ltReason.code, reason_label:ltReason.label,
      duration_min:parseInt(ltDuration)||0,
      period_id:ctx.period?.id, period_no:ctx.period?.period_no,
      date:today, logged_at:nowIso, logged_by:'Supervisor (batch)',
    }));
    const upd = {...pmDb, lostTimeRecords:[...(pmDb.lostTimeRecords||[]), ...recs]};
    setPmDb(upd); LS.set('lostTimeRecords', upd.lostTimeRecords);
    if (apiLive) recs.forEach(r => pmApi.save('pm_lost_time', r));

    setAlert({msg:`Batch logged: ${ltReason.label} · ${ltDuration}min × ${recs.length} workers`,type:'ok'});
    setLtBatchWorkers([]); setLtReason(null); setLtDuration(30); setLtStep('worker'); setLtBatchMode(false);
    setTimeout(()=>scanRef.current?.focus(),100);
  };

  const resetScan = () => {
    setScanStep('period'); setCtx({period:null,line:null,operator:null});
    setStats({scans:0,smvEarned:0,pieces:0}); setScanLog([]);
    setLtStep('worker'); setLtWorker(null); setLtReason(null);
    setTimeout(()=>scanRef.current?.focus(),100);
  };

  // ── Garment unit tracking — 2 checkpoints per permanent garment QR ────────
  // LINE_IN: first process load on the sewing line.
  // FINISHING_OUT: pre-pack, right before poly-bag + carton.
  // Each garment can only be scanned once per checkpoint — a duplicate at
  // the SAME checkpoint is blocked; scanning LINE_IN then later FINISHING_OUT
  // for the same garment is the normal, expected flow.
  const processGarmentScan = useCallback((raw, source='manual') => {
    const v = raw.trim(); if (!v) return;
    setManualBC('');

    const unit = (pmDb.barcodes||[]).find(b=>b.barcode===v && b.barcode_type==='GARMENT_UNIT');
    if (!unit) {
      logInvalidScan(v, `Unknown garment QR (expected ${gtCheckpoint})`, source);
      const entry = { type:'UNKNOWN', val:v, ok:false, error:'Not a recognized garment QR', ts:Date.now(), id:Date.now()+Math.random(), source };
      setScanLog(l=>[entry,...l].slice(0,200));
      if (source==='camera') setTimeout(()=>setScanLog(l=>l.filter(e=>e.id!==entry.id)),1500);
      return;
    }

    const prior = (pmDb.garmentScans||[]).find(s => s.barcode===v && s.checkpoint===gtCheckpoint);
    if (prior) {
      const entry = {
        type:'DUPLICATE', val:v, ok:false, source, ts:Date.now(), id:Date.now()+Math.random(),
        error:`Already scanned at ${gtCheckpoint}`,
        duplicateDetail: prior,
      };
      setScanLog(l=>[entry,...l].slice(0,200));
      return;
    }

    const rec = {
      id:Date.now(), barcode:v, checkpoint:gtCheckpoint,
      style_ref:unit.style_ref, order_no:unit.order_no, size:unit.size, color:unit.color,
      lay_no:unit.lay_no, bundle_no:unit.bundle_no, piece_no:unit.piece_no,
      operator_id:ctx.operator?.operator_id, operator_name:ctx.operator?.name,
      line_id:ctx.line?.line_id, line_name:ctx.line?.line_name,
      factory_id:ctx.line?.factory_id,
      date:new Date().toISOString().slice(0,10),
      scanned_at:new Date().toISOString(),
    };
    const upd = {...pmDb, garmentScans:[...(pmDb.garmentScans||[]),rec]};
    setPmDb(upd); LS.set('garmentScans', upd.garmentScans);
    if (apiLive) pmApi.call('save_garment_scan', rec);

    const entry = { type:gtCheckpoint, val:v, ok:true, data:rec,
      info:`${unit.style_ref} · ${unit.size} · Piece ${unit.piece_no} of B${unit.bundle_no}`,
      ts:Date.now(), id:Date.now()+Math.random(), source };
    setScanLog(l=>[entry,...l].slice(0,200));
    if (navigator.vibrate) navigator.vibrate(40);
    setTimeout(()=>scanRef.current?.focus(),50);
  }, [pmDb.barcodes, pmDb.garmentScans, ctx, gtCheckpoint, apiLive, setPmDb, logInvalidScan]);

  // WIP / shipped derivation for the currently selected style (or all if none selected)
  const garmentTrackStats = useMemo(() => {
    const scans = pmDb.garmentScans || [];
    const relevant = selStyleId ? scans.filter(s=>s.style_ref===styleData?.styleRef) : scans;
    const lineIn = relevant.filter(s=>s.checkpoint==='LINE_IN');
    const finOut = relevant.filter(s=>s.checkpoint==='FINISHING_OUT');
    const finOutBarcodes = new Set(finOut.map(s=>s.barcode));
    const inFactory = lineIn.filter(s=>!finOutBarcodes.has(s.barcode));
    return {
      totalLineIn: lineIn.length,
      totalFinished: finOut.length,
      inFactory: inFactory.length,
    };
  }, [pmDb.garmentScans, selStyleId, styleData]);

  // ── derived data for display ───────────────────────────────────────────────
  const allOpBCs  = (pmDb.barcodes||[]).filter(b=>b.barcode_type==='OPERATION');
  const allGarmentUnits= (pmDb.barcodes||[]).filter(b=>b.barcode_type==='GARMENT_UNIT');
  const allBundles= pmDb.bundles||[];

  // Group barcodes by bundle for print view
  const bundlesWithOps = useMemo(()=>{
    const filtered = selStyleId
      ? allBundles.filter(b=>{
          const plan=(pmDb.cutPlans||[]).find(p=>p.id===b.plan_id);
          return plan?.style_id===selStyleId;
        })
      : allBundles;
    return filtered.map(bnd=>({
      bundle:bnd,
      ops: allOpBCs.filter(o=>o.bundle_id===bnd.id),
      plan:(pmDb.cutPlans||[]).find(p=>p.id===bnd.plan_id),
    }));
  },[allBundles, allOpBCs, pmDb.cutPlans, selStyleId]);

  // ── scan step colour ──────────────────────────────────────────────────────
  const stepInfo = {
    period:   { label:'Period Barcode',   hint:'Scan the period QR to set the time window', color:'#1e40af', bg:'#BBDEFB' },
    line:     { label:'Line Barcode',     hint:'Scan the production line barcode',           color:'#065f46', bg:'#d1fae5' },
    operator: { label:'Operator Barcode', hint:'Scan operator card to assign production',    color:'#5b21b6', bg:'#E1BEE7' },
    bundle:   { label:'Bundle / Op BC',   hint:'Keep scanning operation barcodes',           color:'#92400e', bg:'#FFE082' },
  };
  const si = stepInfo[scanStep];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{moduleCSS}</style>

      <div className="sm-wrap">
        {/* ── Sub-tab bar ── */}
        <div className="sm-tabbar">
          {TABS.map(t=>(
            <button key={t.key} className={`sm-tab ${tab===t.key?'on':''}`}
              onClick={()=>setTab(t.key)}>{t.label}</button>
          ))}
          {/* Style selector — pinned right, visible across all tabs */}
          <div className="sm-style-sel">
            <select className="sel" value={selStyleId}
              onChange={e=>{setSelStyle(e.target.value);setSelPO('');}}>
              <option value="">— Select Style —</option>
              {(db?.mgmtStyles||[]).map(s=>(
                <option key={s.id} value={s.id}>{s.styleRef||s.name||s.id}</option>
              ))}
            </select>
            {styleData?.pos?.length>0 && (
              <select className="sel" value={selPO} onChange={e=>setSelPO(e.target.value)}>
                <option value="">All POs</option>
                {styleData.pos.map(p=>(
                  <option key={p.num} value={p.num}>{p.num} ({p.qty} pcs)</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="sm-body">
          {alert && <Alert msg={alert.msg} type={alert.type} onClose={()=>setAlert(null)} />}

          {/* ══════════════════════════════════════════════════════════
              TAB 1 — CUT PLAN
          ══════════════════════════════════════════════════════════ */}
          {tab==='plan' && (
            <>
              {!styleData && (
                <div className="empty-st">
                  <div className="ei">📋</div>
                  <div className="et">Select a style from the top-right to start</div>
                </div>
              )}

              {styleData && (
                <>
                  {/* Auto-fetched style summary */}
                  <div className="card">
                    <h3>Style Details <SrcBadge src="NIRVIKALP"/></h3>
                    <div className="fetched-block">
                      <div className="block-hdr">📊 From Style Registry · Workstation · Sourcing — read-only</div>
                      <div className="info-grid">
                        {[
                          ['Style Ref', styleData.styleRef],
                          ['Product',   styleData.product||'—'],
                          ['Brand',     styleData.brand||'—'],
                          ['Fabric',    styleData.fabric||'—'],
                          ['Total SMV', `${styleData.totalSMV.toFixed(3)} min`],
                          ['Operations',styleData.opCount],
                          ['Total Qty', styleData.totalQty?.toLocaleString()],
                          ['Buyer',     styleData.buyer||'—'],
                        ].map(([k,v])=>(
                          <div key={k} className="info-cell">
                            <div className="info-lbl">{k}</div>
                            <div className="info-val">{v}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Size × Color matrix */}
                    {sizeColorRows.length>0 && (
                      <>
                        <div className="sec-hdr">Size × Color × Qty (from PO upload)</div>
                        <table className="tbl">
                          <thead><tr><th>PO</th><th>Color</th><th>Size</th><th>Qty</th></tr></thead>
                          <tbody>
                            {sizeColorRows.map((r,i)=>(
                              <tr key={i}>
                                <td style={{fontFamily:'monospace'}}>{r.poNo||selPO}</td>
                                <td>{r.color}</td>
                                <td><span className="pill pill-a">{r.size}</span></td>
                                <td><strong>{r.qty}</strong></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}

                    {/* Operations bulletin */}
                    {styleData.ops.length>0 && (
                      <>
                        <div className="sec-hdr" style={{marginTop:14}}>
                          Operation Bulletin ({styleData.opCount} ops · Total SMV {styleData.totalSMV.toFixed(3)})
                        </div>
                        <table className="tbl">
                          <thead><tr><th>#</th><th>Operation</th><th>Part</th><th>Machine</th><th>SMV</th></tr></thead>
                          <tbody>
                            {styleData.ops.map((op,i)=>(
                              <tr key={i}>
                                <td style={{color:'#6C757D'}}>{i+1}</td>
                                <td>{op.procName}</td>
                                <td>{op.partName||'—'}</td>
                                <td>{op.machine||'—'}</td>
                                <td style={{fontFamily:'monospace',fontWeight:600}}>{op.smv}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}

                    <div style={{marginTop:14}}>
                      <button className="btn btn-p" onClick={createPlan} disabled={saving}>
                        {saving?<span className="spin"/>:null} ➕ Create Cut Plan
                      </button>
                    </div>
                  </div>

                  {/* Existing plans for this style */}
                  {plans.map(plan=>{
                    const planLays    = (pmDb.lays||[]).filter(l=>l.plan_id===plan.id);
                    const planBundles = (pmDb.bundles||[]).filter(b=>b.plan_id===plan.id);
                    const planBCs     = (pmDb.barcodes||[]).filter(b=>b.plan_id===plan.id);
                    return (
                      <div className="card" key={plan.id}>
                        <h3>
                          📋 {plan.style_ref} {plan.order_no&&`· ${plan.order_no}`}
                          <span className="pill pill-am" style={{marginLeft:'auto'}}>
                            {new Date(plan.created_at).toLocaleDateString()}
                          </span>
                        </h3>
                        <Stats items={[
                          {label:'Lays',     val:planLays.length,    color:'#1565C0'},
                          {label:'Bundles',  val:planBundles.length, color:'#388E3C'},
                          {label:'Barcodes', val:planBCs.length,     color:'#6A1B9A'},
                          {label:'Total SMV',val:`${plan.total_smv?.toFixed(2)||0} min`},
                        ]}/>

                        {/* Add lay form */}
                        <div className="new-block">
                          <div className="block-hdr">⚠️ Add Lay</div>
                          <div className="form-row">
                            {[
                              ['Lay No','number','layNo',90],
                              ['Ply From','number','plyFrom',90],
                              ['Ply To','number','plyTo',90],
                              ['Cut Qty','number','cutQty',100],
                              ['Bundle Size','number','bundleQty',100],
                            ].map(([lbl,type,key,w])=>(
                              <div key={key} className="fg">
                                <span className="fl new">{lbl} ★</span>
                                <input type={type} className="inp new-field" style={{width:w}}
                                  value={layForm[key]}
                                  onChange={e=>setLayForm(f=>({...f,[key]:+e.target.value}))}/>
                              </div>
                            ))}
                            <div className="fg">
                              <span className="fl new">Size ★</span>
                              <select className="sel new-field" value={layForm.size}
                                onChange={e=>setLayForm(f=>({...f,size:e.target.value}))}>
                                <option value="">—</option>
                                {styleData.sizes.map(s=><option key={s}>{s}</option>)}
                              </select>
                            </div>
                            <div className="fg">
                              <span className="fl new">Color ★</span>
                              <select className="sel new-field" value={layForm.color}
                                onChange={e=>setLayForm(f=>({...f,color:e.target.value}))}>
                                <option value="">—</option>
                                {styleData.colors.map(c=><option key={c}>{c}</option>)}
                              </select>
                            </div>
                            <button className="btn btn-g" style={{alignSelf:'flex-end'}}
                              onClick={()=>addLay(plan.id)} disabled={saving}>
                              {saving?<span className="spin"/>:null} ➕ Add Lay
                            </button>
                          </div>
                          <div style={{fontSize:11,color:'#92400e',marginTop:4}}>
                            Will create: {Math.ceil(layForm.cutQty/layForm.bundleQty)||0} bundles
                            × {styleData.opCount} op-BCs + {layForm.cutQty} product QRs
                            = <strong>{(Math.ceil(layForm.cutQty/layForm.bundleQty)||0)*styleData.opCount+layForm.cutQty}</strong> barcodes
                          </div>
                        </div>

                        {/* Lay summary table */}
                        {planLays.length>0 && (
                          <table className="tbl" style={{marginTop:12}}>
                            <thead>
                              <tr><th>Lay</th><th>Size</th><th>Color</th>
                                <th>Plies</th><th>Cut Qty</th><th>Bundle Sz</th>
                                <th>Bundles</th><th>Op BCs</th><th>Prod QRs</th></tr>
                            </thead>
                            <tbody>
                              {planLays.map(lay=>{
                                const lb  = (pmDb.bundles||[]).filter(b=>b.plan_id===plan.id&&b.lay_no===lay.layNo);
                                const lob = (pmDb.barcodes||[]).filter(b=>b.plan_id===plan.id&&b.lay_no===lay.layNo&&b.barcode_type==='OPERATION');
                                const lpq = (pmDb.barcodes||[]).filter(b=>b.plan_id===plan.id&&b.lay_no===lay.layNo&&b.barcode_type==='GARMENT_UNIT');
                                return (
                                  <tr key={lay.id}>
                                    <td><strong>Lay {lay.layNo}</strong></td>
                                    <td><span className="pill pill-a">{lay.size}</span></td>
                                    <td>{lay.color||'—'}</td>
                                    <td>{lay.plyFrom}–{lay.plyTo}</td>
                                    <td><strong>{lay.cutQty}</strong></td>
                                    <td>{lay.bundleQty}</td>
                                    <td><span className="pill pill-g">{lb.length}</span></td>
                                    <td><span className="pill pill-2">{lob.length}</span></td>
                                    <td><span className="pill pill-w">{lpq.length}</span></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}

          {/* ══════════════════════════════════════════════════════════
              TAB 2 — LAYS
          ══════════════════════════════════════════════════════════ */}
          {tab==='lays' && (
            <div className="card">
              <h3>📐 All Lays</h3>
              {(pmDb.lays||[]).length===0
                ? <div className="empty-st"><div className="et">No lays yet. Create a cut plan and add lays.</div></div>
                : (
                  <table className="tbl">
                    <thead>
                      <tr><th>Style</th><th>Plan</th><th>Lay</th><th>Size</th><th>Color</th>
                        <th>Plies</th><th>Cut Qty</th><th>Bnd Sz</th><th>Bundles</th><th>Op BCs</th></tr>
                    </thead>
                    <tbody>
                      {(pmDb.lays||[]).map(lay=>{
                        const plan= (pmDb.cutPlans||[]).find(p=>p.id===lay.plan_id);
                        const lb  = (pmDb.bundles||[]).filter(b=>b.plan_id===lay.plan_id&&b.lay_no===lay.layNo);
                        const lob = (pmDb.barcodes||[]).filter(b=>b.plan_id===lay.plan_id&&b.lay_no===lay.layNo&&b.barcode_type==='OPERATION');
                        return (
                          <tr key={lay.id}>
                            <td style={{fontWeight:600}}>{plan?.style_ref||'—'}</td>
                            <td style={{fontFamily:'monospace',fontSize:11,color:'#6C757D'}}>{String(lay.plan_id).slice(-8)}</td>
                            <td>Lay {lay.layNo}</td>
                            <td><span className="pill pill-a">{lay.size}</span></td>
                            <td>{lay.color||'—'}</td>
                            <td>{lay.plyFrom}–{lay.plyTo}</td>
                            <td><strong>{lay.cutQty}</strong></td>
                            <td>{lay.bundleQty}</td>
                            <td><span className="pill pill-g">{lb.length}</span></td>
                            <td><span className="pill pill-2">{lob.length}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              }
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════
              TAB 3 — BARCODES
          ══════════════════════════════════════════════════════════ */}
          {tab==='barcodes' && (
            <div className="card">
              <h3>🔷 Generated Barcodes
                <span className="pill pill-a" style={{marginLeft:'auto'}}>
                  {allOpBCs.length} Op · {allGarmentUnits.length} Prod
                </span>
              </h3>

              {/* Format toggle — Barcode (Code128, scanner guns) vs QR (Qaram-compatible) */}
              <div className="mode-toggle" style={{maxWidth:280,marginBottom:12}}>
                <button className={`mode-btn ${ticketFormat==='barcode'?'on':''}`}
                  onClick={()=>setTicketFormat('barcode')}>▮▯ Barcode</button>
                <button className={`mode-btn ${ticketFormat==='qr'?'on':''}`}
                  onClick={()=>setTicketFormat('qr')}>▢ QR (Qaram)</button>
              </div>

              <Stats items={[
                {label:'Operation BCs', val:allOpBCs.length,   color:'#1565C0'},
                {label:'Product QRs',   val:allGarmentUnits.length, color:'#6A1B9A'},
                {label:'Bundles',       val:allBundles.length, color:'#388E3C'},
                {label:'Total',         val:(pmDb.barcodes||[]).length},
              ]}/>

              {/* Group by bundle */}
              {allBundles.filter(b=>{
                if (!selStyleId) return true;
                const p=(pmDb.cutPlans||[]).find(pl=>pl.id===b.plan_id);
                return p?.style_id===selStyleId;
              }).map(bnd=>{
                const ops  = allOpBCs.filter(o=>o.bundle_id===bnd.id);
                const plan = (pmDb.cutPlans||[]).find(p=>p.id===bnd.plan_id);
                if (!ops.length) return null;
                return (
                  <div key={bnd.id} style={{marginBottom:16}}>
                    <div className="sec-hdr">
                      {plan?.style_ref} · Lay {bnd.lay_no} · Bundle #{bnd.bundle_no}
                      <span className="pill pill-a" style={{marginLeft:8}}>{bnd.size}</span>
                      {bnd.color && <span className="pill pill-2" style={{marginLeft:4}}>{bnd.color}</span>}
                      <span style={{marginLeft:'auto',fontFamily:'monospace',fontSize:10,color:'#ADB5BD'}}>{bnd.qty} pcs</span>
                    </div>
                    <div className="bc-grid">
                      {ops.map((op,i)=>(
                        <TicketCard key={i}
                          label={op.op_name||op.op_code}
                          subLabel={`SMV ${op.smv} · ${op.part_name||''}`}
                          value={op.barcode} format={ticketFormat}
                          qrPayload={QR.bundleOp(op.barcode, plan?.style_ref, bnd.size, op.op_name)}/>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════
              TAB 4 — PRINT
          ══════════════════════════════════════════════════════════ */}
          {tab==='print' && (
            <>
              <div className="card no-print">
                <h3>🖨 Print Bundle Tickets
                  <button className="btn btn-p" style={{marginLeft:'auto'}} onClick={()=>window.print()}>
                    🖨 Print All
                  </button>
                </h3>
                <div style={{fontSize:13,color:'#6C757D'}}>
                  Each ticket shows all operations for that bundle with individual barcodes.
                  {selStyleId && ` Showing ${bundlesWithOps.length} bundles for selected style.`}
                </div>

                {/* Period setup inside print tab */}
                <div className="new-block" style={{marginTop:14}}>
                  <div className="block-hdr">⏱ Period Barcodes (generate before printing)</div>
                  <div className="form-row">
                    <div className="fg">
                      <span className="fl new">Date ★</span>
                      <input type="date" className="inp new-field" style={{width:160}}
                        value={pf.date} onChange={e=>setPF(f=>({...f,date:e.target.value}))}/>
                    </div>
                    <div className="fg">
                      <span className="fl new">Shift Hours ★</span>
                      <input type="number" className="inp new-field" style={{width:110}}
                        value={pf.workHrs} onChange={e=>setPF(f=>({...f,workHrs:+e.target.value}))}/>
                    </div>
                    <div className="fg">
                      <span className="fl new">Period Duration (hr) ★</span>
                      <input type="number" step="0.5" className="inp new-field" style={{width:130}}
                        value={pf.durHrs} onChange={e=>setPF(f=>({...f,durHrs:+e.target.value}))}/>
                    </div>
                    <div className="fg">
                      <span className="fl new">Line ★</span>
                      <select className="sel new-field" value={String(pf.lineId||'')}
                        onChange={e=>setPF(f=>({...f,lineId:e.target.value}))}>
                        <option value="">— Select Line —</option>
                        {allLines.map(l=>(
                          <option key={`${l.factoryId}-${l.id}`} value={String(l.id)}>
                            {l.factoryName} · {l.name||`Line ${l.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button className="btn btn-p" style={{alignSelf:'flex-end'}}
                      onClick={genPeriods} disabled={saving}>
                      {saving?<span className="spin"/>:null} Generate
                    </button>
                  </div>
                  <div style={{fontSize:12,color:'#92400e',background:'#FFE082',
                    borderRadius:5,padding:'6px 10px',marginTop:6}}>
                    {pf.workHrs}h × 60 = {pf.workHrs*60}m − 40m breaks = {nm}m net
                    ÷ {Math.floor(pf.durHrs*60)}m/period = <strong>{np} periods</strong>
                  </div>
                </div>

                {/* Period BCs */}
                {todayPers.length>0 && (
                  <>
                    <div className="sec-hdr" style={{marginTop:14}}>Period Barcodes — {pf.date}</div>
                    <div className="bc-grid">
                      {todayPers.map(p=>(
                        <BarcodeCard key={p.id} label={`Period ${p.period_no}`}
                          subLabel={`${p.duration_min} min · ${p.date}`}
                          value={p.barcode} type="barcode"/>
                      ))}
                    </div>
                  </>
                )}

                {/* Line + Worker BCs */}
                {(pmDb.lineConfigs||[]).length>0 && (
                  <>
                    <div className="sec-hdr" style={{marginTop:14}}>Line Barcodes</div>
                    <div className="bc-grid">
                      {(pmDb.lineConfigs||[]).map(l=>(
                        <BarcodeCard key={l.line_id} label={l.line_name}
                          subLabel={l.factory_name} value={l.barcode} type="barcode"/>
                      ))}
                    </div>
                  </>
                )}
                {(pmDb.workers||[]).length>0 && (
                  <>
                    <div className="sec-hdr" style={{marginTop:14}}>
                      Operator Barcodes
                      <span style={{marginLeft:8,fontSize:10,fontWeight:400,color:'#ADB5BD'}}>
                        {(pmDb.workers||[]).length} employees total
                      </span>
                    </div>

                    {/* Group/filter controls */}
                    <div className="new-block" style={{marginBottom:10}}>
                      <div className="block-hdr">Print Selection</div>
                      <div className="form-row">
                        <div className="fg">
                          <span className="fl">Group By</span>
                          <select className="sel" value={empGroupBy}
                            onChange={e=>{setEmpGroupBy(e.target.value);setEmpFilterVal('');setEmpSelected(new Set());}}>
                            <option value="all">All Employees</option>
                            <option value="factory">Factory</option>
                            <option value="department">Department</option>
                            <option value="line">Line</option>
                            <option value="individual">Individual Selection</option>
                          </select>
                        </div>
                        {['factory','department','line'].includes(empGroupBy) && (
                          <div className="fg">
                            <span className="fl">Select {empGroupBy}</span>
                            <select className="sel" value={empFilterVal}
                              onChange={e=>setEmpFilterVal(e.target.value)}>
                              <option value="">— Choose —</option>
                              {empFilterOptions.map(v=><option key={v} value={v}>{v}</option>)}
                            </select>
                          </div>
                        )}
                      </div>

                      {empGroupBy==='individual' && (
                        <div style={{maxHeight:180,overflowY:'auto',border:'1px solid #DEE2E6',
                          borderRadius:6,padding:8,background:'#fff',marginTop:6}}>
                          {(pmDb.workers||[]).map(w=>(
                            <label key={w.id} style={{display:'flex',alignItems:'center',gap:6,
                              padding:'3px 0',fontSize:12,cursor:'pointer'}}>
                              <input type="checkbox" checked={empSelected.has(w.id)}
                                onChange={e=>{
                                  setEmpSelected(prev=>{
                                    const next = new Set(prev);
                                    e.target.checked ? next.add(w.id) : next.delete(w.id);
                                    return next;
                                  });
                                }}/>
                              {w.name} · {w.operator_id}
                            </label>
                          ))}
                        </div>
                      )}

                      <div style={{marginTop:8,fontSize:12,color:'#92400e'}}>
                        Showing <strong>{filteredWorkers.length}</strong> of {(pmDb.workers||[]).length} employees
                        {filteredWorkers.length>200 && (
                          <span style={{color:'#C62828',fontWeight:700}}>
                            {' '}— large batch, printing may take a moment
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="bc-grid">
                      {filteredWorkers.slice(0,500).map(w=>(
                        <BarcodeCard key={w.id} label={w.name}
                          subLabel={`${w.operator_id} · ${w.designation||''}`}
                          value={w.worker_barcode} type="barcode"/>
                      ))}
                    </div>
                    {filteredWorkers.length>500 && (
                      <div style={{fontSize:11,color:'#C62828',marginTop:6}}>
                        Showing first 500 — narrow your filter to print the rest, or print in batches.
                      </div>
                    )}
                  </>
                )}

                {/* Lost Time Reason tickets — fixed set of 12, print once, reuse always.
                    Supervisor scans one of these against a worker to log lost time. */}
                <div className="sec-hdr" style={{marginTop:14}}>
                  ⏱ Lost Time Reason Tickets
                  <span style={{marginLeft:8,fontSize:10,fontWeight:400,color:'#ADB5BD'}}>
                    Print once — reusable for all lost time entries
                  </span>
                </div>
                <div className="bc-grid">
                  {lostTimeReasons.map(r=>(
                    <BarcodeCard key={r.code} label={r.code}
                      subLabel={r.label}
                      value={BC.lostTime(r.code)} type="barcode"/>
                  ))}
                </div>
              </div>

              {/* ── PRINTABLE BUNDLE TICKETS — A4 layout ── */}
              <div id="print-area" className="a4-sheet">
                {bundlesWithOps.length===0
                  ? <div className="empty-st no-print">
                      <div className="ei">🖨</div>
                      <div className="et">No bundles yet. Create cut plan and add lays first.</div>
                    </div>
                  : bundlesWithOps.map(({bundle,ops,plan})=>(
                      <BundleTicket key={bundle.id}
                        bundle={bundle} ops={ops}
                        styleRef={plan?.style_ref||'—'}
                        poNo={plan?.order_no||''}
                        styleQty={styleData?.totalQty}
                        orderQty={styleData?.pos?.find(p=>p.num===plan?.order_no)?.qty}
                        deliveryDate={styleData?.pos?.find(p=>p.num===plan?.order_no)?.date}
                        country={styleData?._raw?.country||styleData?._raw?.destinationCountry||''}
                      />
                    ))
                }
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════
              TAB 5 — SCAN
          ══════════════════════════════════════════════════════════ */}
          {tab==='scan' && (
            <div className="scan-layout">

              {/* ── LEFT PANEL — context + input ── */}
              <div className="scan-left">

                {/* Mode toggle — Production / Lost Time / Garment Unit Tracking */}
                <div className="mode-toggle">
                  <button className={`mode-btn ${scanMode==='production'?'on':''}`}
                    onClick={()=>setScanMode('production')}>📦 Production</button>
                  <button className={`mode-btn ${scanMode==='lostTime'?'on':''}`}
                    onClick={()=>setScanMode('lostTime')}>⏱ Lost Time</button>
                  <button className={`mode-btn ${scanMode==='garmentTrack'?'on':''}`}
                    onClick={()=>setScanMode('garmentTrack')}>🏭 Garment Track</button>
                </div>

                {scanMode==='production' ? (
                <>
                {/* Step indicator */}
                <div className="scan-steps">
                  {['period','line','operator','bundle'].map((s,i)=>{
                    const done  = (s==='period'&&ctx.period)||(s==='line'&&ctx.line)||(s==='operator'&&ctx.operator);
                    const active= scanStep===s;
                    const si2   = stepInfo[s];
                    return (
                      <div key={s} className={`scan-step ${active?'active':''} ${done?'done':''}`}
                        style={active?{borderColor:si2.color,background:si2.bg}:{}}>
                        <span className="step-num" style={active||done?{background:si2.color}:{}}>
                          {done?'✓':(i+1)}
                        </span>
                        <span className="step-lbl">{si2.label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Context badges */}
                <div className="ctx-panel">
                  {[
                    ['Period', ctx.period ? `#${ctx.period.period_no} · ${ctx.period.duration_min}m` : null],
                    ['Line',   ctx.line   ? ctx.line.line_name   : null],
                    ['Operator',ctx.operator?`${ctx.operator.name} · ${ctx.operator.operator_id}`:null],
                  ].map(([k,v])=>(
                    <div key={k} className={`ctx-row ${v?'set':''}`}>
                      <span className="ctx-key">{k}</span>
                      <span className="ctx-val">{v||'—'}</span>
                    </div>
                  ))}
                </div>

                {/* Current step prompt */}
                <div className="step-prompt" style={{borderColor:si.color,background:si.bg}}>
                  <div style={{fontWeight:700,color:si.color,fontSize:13}}>{si.label}</div>
                  <div style={{fontSize:11,color:'#6C757D',marginTop:3}}>{si.hint}</div>
                </div>

                {/* Scan input */}
                <div style={{display:'flex',gap:6,marginTop:8}}>
                  <input ref={scanRef} className="inp scan-inp"
                    value={manualBC} onChange={e=>setManualBC(e.target.value)}
                    onKeyDown={e=>{if(e.key==='Enter')processScan(manualBC,'manual');}}
                    placeholder={`Scan ${scanStep} barcode…`}
                    style={{flex:1,fontSize:15,padding:'9px 12px'}}/>
                  <button className="btn btn-p" onClick={()=>processScan(manualBC,'manual')}>GO</button>
                </div>
                </>
                ) : scanMode==='lostTime' ? (
                <>
                {/* ── LOST TIME ENTRY MODE ── */}
                <div className="lt-banner">
                  Supervisor scans a worker, then a reason ticket, then enters duration.
                  This does <b>not</b> affect the worker's efficiency — it reduces
                  their available time fairly. Section efficiency reflects the loss.
                </div>

                <div className="mode-toggle" style={{marginBottom:6}}>
                  <button className={`mode-btn ${!ltBatchMode?'on':''}`}
                    onClick={()=>{setLtBatchMode(false);setLtBatchWorkers([]);}}>
                    👤 Single Worker
                  </button>
                  <button className={`mode-btn ${ltBatchMode?'on':''}`}
                    onClick={()=>{setLtBatchMode(true);setLtWorker(null);setLtStep('reason');}}>
                    👥 Whole-Line Event
                  </button>
                </div>

                {ltBatchMode && (
                  <div className="lt-banner" style={{background:'#FFF8E1',borderColor:'#fcd34d',color:'#92400e',marginBottom:6}}>
                    For power failure, no-material or other whole-line stoppages.
                    Sweep-scan every affected worker, then pick one reason+duration
                    for all of them — keeps cost impact accurate to the real headcount.
                  </div>
                )}

                {!ltBatchMode ? (
                <>
                <div className="scan-steps">
                  {['worker','reason','duration'].map((s,i)=>{
                    const done  = (s==='worker'&&ltWorker)||(s==='reason'&&ltReason);
                    const active= ltStep===s;
                    const col = {worker:'#2E7D32',reason:'#FF8F00',duration:'#6366f1'}[s];
                    return (
                      <div key={s} className={`scan-step ${active?'active':''} ${done?'done':''}`}
                        style={active?{borderColor:col,background:col+'22'}:{}}>
                        <span className="step-num" style={active||done?{background:col}:{}}>
                          {done?'✓':(i+1)}
                        </span>
                        <span className="step-lbl">{s[0].toUpperCase()+s.slice(1)}</span>
                      </div>
                    );
                  })}
                </div>

                <div className="ctx-panel">
                  <div className={`ctx-row ${ltWorker?'set':''}`}>
                    <span className="ctx-key">Worker</span>
                    <span className="ctx-val">{ltWorker?`${ltWorker.name} · ${ltWorker.operator_id}`:'—'}</span>
                  </div>
                  <div className={`ctx-row ${ltReason?'set':''}`}>
                    <span className="ctx-key">Reason</span>
                    <span className="ctx-val">{ltReason?`${ltReason.code} · ${ltReason.label}`:'—'}</span>
                  </div>
                </div>

                {ltStep!=='duration' ? (
                  <>
                    <div className="step-prompt" style={{borderColor:'#6366f1',background:'#eef2ff'}}>
                      <div style={{fontWeight:700,color:'#6366f1',fontSize:13}}>
                        Scan {ltStep==='worker'?'Worker Barcode':'Reason Ticket'}
                      </div>
                    </div>
                    <div style={{display:'flex',gap:6,marginTop:8}}>
                      <input ref={scanRef} className="inp scan-inp"
                        value={manualBC} onChange={e=>setManualBC(e.target.value)}
                        onKeyDown={e=>{if(e.key==='Enter')processLostTimeScan(manualBC);}}
                        placeholder={`Scan ${ltStep} barcode…`}
                        style={{flex:1,fontSize:15,padding:'9px 12px'}}/>
                      <button className="btn btn-p" onClick={()=>processLostTimeScan(manualBC)}>GO</button>
                    </div>
                  </>
                ) : (
                  <div className="new-block" style={{marginTop:8}}>
                    <div className="block-hdr">Enter Duration (minutes)</div>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <input type="number" className="inp new-field" style={{width:100}}
                        value={ltDuration} onChange={e=>setLtDuration(e.target.value)}/>
                      <button className="btn btn-g" onClick={submitLostTime}>
                        ✓ Log Lost Time
                      </button>
                    </div>
                  </div>
                )}
                </>
                ) : (
                <>
                {/* Batch flow: reason first (applies to everyone), then sweep-scan workers */}
                <div className="ctx-panel">
                  <div className={`ctx-row ${ltReason?'set':''}`}>
                    <span className="ctx-key">Reason</span>
                    <span className="ctx-val">{ltReason?`${ltReason.code} · ${ltReason.label}`:'—'}</span>
                  </div>
                  <div className="ctx-row set">
                    <span className="ctx-key">Workers Scanned</span>
                    <span className="ctx-val">{ltBatchWorkers.length}</span>
                  </div>
                </div>

                {!ltReason ? (
                  <div className="step-prompt" style={{borderColor:'#FF8F00',background:'#FFF8E1'}}>
                    <div style={{fontWeight:700,color:'#FF8F00',fontSize:13}}>Scan Reason Ticket First</div>
                  </div>
                ) : (
                  <div className="step-prompt" style={{borderColor:'#2E7D32',background:'#f0fdf4'}}>
                    <div style={{fontWeight:700,color:'#2E7D32',fontSize:13}}>Sweep-Scan Affected Workers</div>
                  </div>
                )}

                <div style={{display:'flex',gap:6,marginTop:8}}>
                  <input ref={scanRef} className="inp scan-inp"
                    value={manualBC} onChange={e=>setManualBC(e.target.value)}
                    onKeyDown={e=>{
                      if(e.key!=='Enter') return;
                      if (!ltReason) processLostTimeScan(manualBC);
                      else { addBatchWorker(manualBC); setManualBC(''); }
                    }}
                    placeholder={ltReason ? "Scan worker barcode…" : "Scan reason ticket…"}
                    style={{flex:1,fontSize:15,padding:'9px 12px'}}/>
                  <button className="btn btn-p" onClick={()=>{
                    if (!ltReason) processLostTimeScan(manualBC);
                    else { addBatchWorker(manualBC); setManualBC(''); }
                  }}>GO</button>
                </div>

                {ltBatchWorkers.length>0 && (
                  <div className="cam-batch-panel" style={{marginTop:8,background:'#F8F9FA',border:'1px solid #DEE2E6'}}>
                    <div className="cam-batch-chips">
                      {ltBatchWorkers.map(w=>(
                        <span key={w.operator_id} className="cam-chip" style={{background:'#eff6ff',borderColor:'#90CAF9',color:'#1e40af'}}>
                          {w.name}
                          <button onClick={()=>setLtBatchWorkers(prev=>prev.filter(x=>x.operator_id!==w.operator_id))}>✕</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {ltReason && ltBatchWorkers.length>0 && (
                  <div className="new-block" style={{marginTop:8}}>
                    <div className="block-hdr">Duration for all {ltBatchWorkers.length} workers (minutes)</div>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <input type="number" className="inp new-field" style={{width:100}}
                        value={ltDuration} onChange={e=>setLtDuration(e.target.value)}/>
                      <button className="btn btn-g" onClick={submitBatchLostTime}>
                        ✓ Log for {ltBatchWorkers.length} Workers
                      </button>
                    </div>
                  </div>
                )}

                {/* Camera sweep-scan for batch worker collection */}
                <button className="btn btn-cam" style={{width:'100%',marginTop:6}}
                  onClick={()=>setShowCamera(true)}>
                  📷 Camera Sweep-Scan Workers
                </button>
                </>
                )}
                </>
                ) : (
                <>
                {/* ── GARMENT UNIT TRACKING MODE ── */}
                <div className="lt-banner" style={{background:'#f0fdf4',borderColor:'#86efac',color:'#166534'}}>
                  Scan the permanent garment QR at one of two checkpoints.
                  Each garment can only be scanned once per checkpoint.
                  Use <b>Batch Scan</b> below for scanning many garments quickly.
                </div>

                <div className="mode-toggle" style={{marginBottom:4}}>
                  <button className={`mode-btn ${gtCheckpoint==='LINE_IN'?'on':''}`}
                    style={gtCheckpoint==='LINE_IN'?{background:'#0ea5e9',borderColor:'#0ea5e9'}:{}}
                    onClick={()=>setGtCheckpoint('LINE_IN')}>🏭 Line-In</button>
                  <button className={`mode-btn ${gtCheckpoint==='FINISHING_OUT'?'on':''}`}
                    style={gtCheckpoint==='FINISHING_OUT'?{background:'#388E3C',borderColor:'#388E3C'}:{}}
                    onClick={()=>setGtCheckpoint('FINISHING_OUT')}>📦 Finishing-Out</button>
                </div>

                <div className="ctx-panel">
                  <div className={`ctx-row ${ctx.line?'set':''}`}>
                    <span className="ctx-key">Line</span>
                    <span className="ctx-val">{ctx.line?.line_name||'— (optional)'}</span>
                  </div>
                  <div className={`ctx-row ${ctx.operator?'set':''}`}>
                    <span className="ctx-key">Operator</span>
                    <span className="ctx-val">{ctx.operator?.name||'— (optional)'}</span>
                  </div>
                </div>

                <div className="step-prompt" style={{
                  borderColor:gtCheckpoint==='LINE_IN'?'#0ea5e9':'#388E3C',
                  background:gtCheckpoint==='LINE_IN'?'#eff6ff':'#f0fdf4'}}>
                  <div style={{fontWeight:700,fontSize:13,
                    color:gtCheckpoint==='LINE_IN'?'#0369a1':'#166534'}}>
                    Scan Garment QR — {gtCheckpoint==='LINE_IN'?'Line Load':'Finishing / Pre-Pack'}
                  </div>
                </div>

                <div style={{display:'flex',gap:6,marginTop:8}}>
                  <input ref={scanRef} className="inp scan-inp"
                    value={manualBC} onChange={e=>setManualBC(e.target.value)}
                    onKeyDown={e=>{if(e.key==='Enter')processGarmentScan(manualBC,'manual');}}
                    placeholder="Scan garment QR…"
                    style={{flex:1,fontSize:15,padding:'9px 12px'}}/>
                  <button className="btn btn-p" onClick={()=>processGarmentScan(manualBC,'manual')}>GO</button>
                </div>

                <div className="eff-panel" style={{marginTop:8}}>
                  <div style={{fontWeight:700,fontSize:12,marginBottom:8,color:'#2C3E50'}}>
                    Garment Tracking {selStyleId?`— ${styleData?.styleRef}`:'(all styles)'}
                  </div>
                  {[
                    ['Line-In Total',    garmentTrackStats.totalLineIn],
                    ['In Factory (WIP)', garmentTrackStats.inFactory],
                    ['Finished Today',   garmentTrackStats.totalFinished],
                  ].map(([k,v])=>(
                    <div key={k} style={{display:'flex',justifyContent:'space-between',
                      fontSize:12,padding:'4px 0',borderBottom:'1px solid #F4F6F8'}}>
                      <span style={{color:'#6C757D'}}>{k}</span>
                      <span style={{fontWeight:700,fontFamily:'monospace',fontSize:14}}>{v}</span>
                    </div>
                  ))}
                </div>
                </>
                )}

                {/* Camera scan toggle */}
                <button className="btn btn-cam" style={{width:'100%',marginTop:6}}
                  onClick={()=>setShowCamera(true)}>
                  📷 Scan with Camera
                </button>

                {/* Batch scan — for scanning many operation barcodes off a
                    printed bundle ticket sheet, or many garment QRs at a
                    checkpoint, in one session instead of one at a time. */}
                {((scanMode==='production' && scanStep==='bundle') || scanMode==='garmentTrack') && (
                  <button className="btn btn-cam" style={{width:'100%',marginTop:6,background:'#166534'}}
                    onClick={()=>setShowBatchCamera(true)}>
                    📄 Batch Scan {scanMode==='garmentTrack'?'Garments':'Whole Page'}
                  </button>
                )}

                {showCamera && (
                  <CameraScanner
                    onResult={(val)=>{
                      if (scanMode==='lostTime' && ltBatchMode) addBatchWorker(val);
                      else if (scanMode==='lostTime') processLostTimeScan(val);
                      else if (scanMode==='garmentTrack') processGarmentScan(val,'camera');
                      else processScan(val,'camera');
                    }}
                    onClose={()=>setShowCamera(false)}
                  />
                )}

                {showBatchCamera && (
                  <CameraScanner
                    batchMode={true}
                    onBatchSubmit={(codes)=>{
                      // Process each accumulated code in sequence through the
                      // normal scan pipeline, so duplicate detection and
                      // efficiency calc all still apply per barcode.
                      if (scanMode==='garmentTrack') {
                        codes.forEach(code => processGarmentScan(code,'camera'));
                      } else {
                        codes.forEach(code => processScan(code,'camera'));
                      }
                      setShowBatchCamera(false);
                      setAlert({msg:`Processed ${codes.length} scans from batch`,type:'ok'});
                    }}
                    onClose={()=>setShowBatchCamera(false)}
                  />
                )}

                <button className="btn btn-s" style={{width:'100%',marginTop:6}}
                  onClick={resetScan}>🔄 Reset Scan Session</button>

                {/* Dual efficiency panel: worker (fair) vs section (raw/exposed) */}
                <div className="eff-panel">
                  <div style={{fontWeight:700,fontSize:12,marginBottom:8,color:'#2C3E50'}}>
                    Live Session Stats
                  </div>
                  <div style={{display:'flex',gap:10}}>
                    <div style={{textAlign:'center',flex:1}}>
                      <EffRing value={operatorEff} size={62}/>
                      <div style={{fontSize:9,color:'#6C757D',marginTop:2}}>Worker Eff (fair)</div>
                    </div>
                    <div style={{textAlign:'center',flex:1}}>
                      <EffRing value={sectionEff} size={62}/>
                      <div style={{fontSize:9,color:'#6C757D',marginTop:2}}>Section Eff (raw)</div>
                    </div>
                  </div>
                  <div style={{marginTop:10}}>
                    {[
                      ['Scans',      sessionStats.scans],
                      ['Pieces',     sessionStats.pieces],
                      ['SMV Earned', sessionStats.smvEarned.toFixed(2)+' min'],
                      ['Avail Min',  availMin],
                      ['Workers',    workers],
                      ['Worker Lost Min', operatorLostMin],
                      ['Section Lost Min', sectionLostMin],
                    ].map(([k,v])=>(
                      <div key={k} style={{display:'flex',justifyContent:'space-between',
                        fontSize:11,padding:'2px 0',borderBottom:'1px solid #F4F6F8'}}>
                        <span style={{color:'#6C757D'}}>{k}</span>
                        <span style={{fontWeight:600,fontFamily:'monospace'}}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:8,fontSize:9,color:'#ADB5BD',lineHeight:1.4}}>
                    Worker Eff = SMV ÷ (Avail − their Lost Time). Fair to the worker.<br/>
                    Section Eff = SMV ÷ raw Avail (unadjusted). Exposes waste.
                  </div>
                </div>

                {/* Resource waste intelligence — where time is being lost */}
                {lostTimeByReason.length>0 && (
                  <div className="eff-panel" style={{marginTop:8}}>
                    <div style={{fontWeight:700,fontSize:12,marginBottom:2,color:'#2C3E50'}}>
                      ⚠ Resource Waste Today {ctx.line?`· ${ctx.line.line_name}`:''}
                    </div>
                    <div style={{fontSize:10,color:'#b45309',marginBottom:8}}>
                      {sectionLostPct}% of section capacity lost
                    </div>
                    {lostTimeByReason.map(r=>(
                      <div key={r.code} style={{display:'flex',justifyContent:'space-between',
                        fontSize:11,padding:'3px 0',borderBottom:'1px solid #F4F6F8'}}>
                        <span style={{color:'#2C3E50'}}>{r.code} {r.label}</span>
                        <span style={{fontWeight:700,color:'#C62828'}}>{r.minutes}m</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* API status */}
                <div style={{marginTop:8,fontSize:10,
                  color:apiLive?'#15803d':'#b45309',
                  background:apiLive?'#f0fdf4':'#FFF8E1',
                  border:`1px solid ${apiLive?'#86efac':'#fcd34d'}`,
                  padding:'4px 8px',borderRadius:5}}>
                  {apiLive?'● API Live — scans saving to pm_api.php':'○ Local mode — scans in localStorage only'}
                </div>
              </div>

              {/* ── RIGHT PANEL — scan log ── */}
              <div className="scan-right">
                <div style={{display:'flex',justifyContent:'space-between',
                  alignItems:'center',marginBottom:8}}>
                  <div style={{display:'flex',gap:4}}>
                    <button className={`log-tab ${logView==='log'?'on':''}`}
                      onClick={()=>setLogView('log')}>
                      Scan Log ({scanLog.length})
                    </button>
                    <button className={`log-tab ${logView==='invalid'?'on':''}`}
                      onClick={()=>setLogView('invalid')}>
                      Invalid ({(pmDb.invalidScans||[]).length})
                    </button>
                  </div>
                  {logView==='log' && scanLog.length>0 && (
                    <button className="btn btn-s" style={{fontSize:11,padding:'3px 8px'}}
                      onClick={()=>setScanLog([])}>Clear</button>
                  )}
                </div>

                {logView==='log' && (
                  <>
                    {scanLog.length===0 && (
                      <div style={{color:'#ADB5BD',fontSize:12,padding:'20px 0',textAlign:'center'}}>
                        Waiting for first scan…
                      </div>
                    )}
                    {scanLog.map((entry)=>(
                      <ScanRow key={entry.id||entry.ts} entry={entry}/>
                    ))}
                  </>
                )}

                {logView==='invalid' && (
                  <>
                    <div style={{fontSize:10,color:'#ADB5BD',marginBottom:8}}>
                      Auto-clears entries older than 7 days
                    </div>
                    {(pmDb.invalidScans||[]).length===0 && (
                      <div style={{color:'#ADB5BD',fontSize:12,padding:'20px 0',textAlign:'center'}}>
                        No invalid scans recorded
                      </div>
                    )}
                    {(pmDb.invalidScans||[]).map((s,i)=>(
                      <div key={i} style={{display:'flex',alignItems:'center',gap:8,
                        padding:'5px 10px',borderRadius:6,background:'#FFEBEE',
                        border:'1px solid #fca5a5',fontSize:11,marginBottom:4}}>
                        <span style={{fontFamily:'monospace',flex:1,wordBreak:'break-all'}}>{s.val}</span>
                        <span style={{color:'#C62828',fontSize:10}}>{s.reason}</span>
                        <span style={{fontSize:9,padding:'1px 5px',borderRadius:8,
                          background:s.source==='camera'?'#BBDEFB':'#F4F6F8',
                          color:s.source==='camera'?'#1e40af':'#6C757D'}}>{s.source}</span>
                        <span style={{color:'#ADB5BD',fontSize:10}}>
                          {new Date(s.ts).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {/* Period summary table */}
                {sessionStats.scans>0 && (
                  <div style={{marginTop:14}}>
                    <div className="sec-hdr">Operations Scanned This Session</div>
                    <table className="tbl">
                      <thead>
                        <tr><th>Op</th><th>Bundle</th><th>Operator</th><th>Pcs</th><th>SMV</th></tr>
                      </thead>
                      <tbody>
                        {scanLog.filter(e=>e.ok&&e.type==='OPERATION').slice(0,50).map((e,i)=>(
                          <tr key={i}>
                            <td>{e.data?.op_name||e.data?.op_code||'—'}</td>
                            <td style={{fontFamily:'monospace',fontSize:11}}>
                              B{e.data?.bundle_no} L{e.data?.lay_no}
                            </td>
                            <td>{e.data?.operator_name||e.data?.operator_id||'—'}</td>
                            <td>{e.data?.pieces_counted||0}</td>
                            <td style={{fontFamily:'monospace'}}>{(e.data?.smv_earned||0).toFixed(3)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════
              TAB — GARMENT QR (permanent per-piece heat-transfer QR)
              Printed/exported SEPARATELY from bundle operation tickets.
          ══════════════════════════════════════════════════════════ */}
          {tab==='garment' && (
            <>
              <div className="card no-print">
                <h3>🏷 Garment Unit QR Export
                  <span className="pill pill-a" style={{marginLeft:'auto'}}>
                    {allGarmentUnits.length} pieces generated
                  </span>
                </h3>
                <div style={{fontSize:13,color:'#6C757D',marginBottom:12}}>
                  One permanent QR per physical garment, meant for <b>heat transfer</b>
                  near the main label. Carries only the bare permanent number —
                  no descriptive text — so the QR stays small and durable through
                  repeated washing. All context (style, PO, size, color, lay,
                  bundle) is looked up from this number, not printed inside it.
                  This export is separate from the bundle operation tickets in
                  the Print tab, since heat-transfer printing is a different
                  physical process from paper bundle tickets.
                </div>

                {selStyleId ? (
                  <>
                    <Stats items={[
                      {label:'Total Pieces',  val:allGarmentUnits.length, color:'#6A1B9A'},
                      {label:'Line-In Today', val:garmentTrackStats.totalLineIn, color:'#0ea5e9'},
                      {label:'In Factory',    val:garmentTrackStats.inFactory,   color:'#FF8F00'},
                      {label:'Finished',      val:garmentTrackStats.totalFinished, color:'#388E3C'},
                    ]}/>

                    <div style={{display:'flex',gap:8,marginBottom:14}}>
                      <button className="btn btn-p" onClick={()=>window.print()}>
                        🖨 Print QR Sheet (PDF)
                      </button>
                      <button className="btn btn-s" onClick={()=>{
                        downloadCSV(
                          `garment_units_${styleData?.styleRef||'export'}.csv`,
                          allGarmentUnits,
                          [
                            {key:'barcode',   label:'Garment QR'},
                            {key:'style_ref', label:'Style'},
                            {key:'order_no',  label:'PO'},
                            {key:'lay_no',    label:'Lay'},
                            {key:'bundle_no', label:'Bundle'},
                            {key:'piece_no',  label:'Piece'},
                            {key:'size',      label:'Size'},
                            {key:'color',     label:'Color'},
                            {key:'created_at',label:'Created'},
                          ]
                        );
                      }}>
                        📊 Export CSV (Excel)
                      </button>
                    </div>

                    {/* On-screen preview grid — compact, many per row since
                        these are small heat-transfer labels, not full tickets */}
                    <div className="garment-qr-grid">
                      {allGarmentUnits.map(u=>(
                        <div key={u.barcode} className="garment-qr-cell">
                          <img src={qrUrl(u.barcode, 90, 'H')} width={90} height={90} alt="QR"/>
                          <div className="garment-qr-meta">
                            {u.style_ref} · {u.size}<br/>
                            B{u.bundle_no}-P{u.piece_no}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="empty-st">
                    <div className="ei">🏷</div>
                    <div className="et">Select a style (top-right) to see its garment QR codes</div>
                  </div>
                )}
              </div>

              {/* Print-only area — isolated for PDF export via browser print */}
              <div id="garment-print-area" className="garment-qr-grid">
                {allGarmentUnits.map(u=>(
                  <div key={u.barcode} className="garment-qr-cell">
                    <img src={qrUrl(u.barcode, 90, 'H')} width={90} height={90} alt="QR"/>
                    <div className="garment-qr-meta">
                      {u.style_ref} · {u.size} · B{u.bundle_no}-P{u.piece_no}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════
              TAB 6 — SETUP (Lost Time Reasons CRUD)
          ══════════════════════════════════════════════════════════ */}
          {tab==='setup' && (
            <div className="card">
              <h3>⚙️ Lost Time Reasons
                <span className="pill pill-a" style={{marginLeft:'auto'}}>{lostTimeReasons.length} reasons</span>
              </h3>
              <div style={{fontSize:12,color:'#6C757D',marginBottom:12}}>
                Add, edit, or remove lost time reason codes. Each reason gets its own
                printable barcode ticket that supervisors scan against a worker.
                Changing a reason's label does not change its barcode — existing
                printed tickets stay valid.
              </div>

              <table className="tbl">
                <thead>
                  <tr><th style={{width:70}}>Code</th><th>Reason Label</th><th style={{width:100}}>Barcode</th><th style={{width:140}}>Actions</th></tr>
                </thead>
                <tbody>
                  {lostTimeReasons.map(r=>(
                    <tr key={r.code}>
                      <td><span className="pill pill-2">{r.code}</span></td>
                      <td>
                        <input className="inp" style={{width:'100%'}}
                          defaultValue={r.label}
                          onBlur={e=>{
                            if (e.target.value.trim() && e.target.value!==r.label) {
                              updateLostTimeReason(r.code, e.target.value.trim());
                            }
                          }}/>
                      </td>
                      <td style={{fontFamily:'monospace',fontSize:11,color:'#ADB5BD'}}>
                        {BC.lostTime(r.code)}
                      </td>
                      <td>
                        <button className="btn btn-s" style={{fontSize:11,padding:'4px 10px'}}
                          onClick={()=>{
                            if (window.confirm(`Delete reason "${r.code} · ${r.label}"? Existing lost time records referencing it will keep the old label.`)) {
                              deleteLostTimeReason(r.code);
                            }
                          }}>🗑 Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button className="btn btn-p" style={{marginTop:12}} onClick={addLostTimeReason}>
                ➕ Add Reason
              </button>

              <div className="sec-hdr" style={{marginTop:20}}>
                🖨 Printable Reason Tickets
                <span style={{marginLeft:8,fontSize:10,fontWeight:400,color:'#ADB5BD'}}>
                  Give these to supervisors — reusable indefinitely
                </span>
              </div>
              <div className="bc-grid">
                {lostTimeReasons.map(r=>(
                  <BarcodeCard key={r.code} label={r.code} subLabel={r.label}
                    value={BC.lostTime(r.code)} type="barcode"/>
                ))}
              </div>
              <button className="btn btn-p" style={{marginTop:12}}
                onClick={()=>{ setTab('print'); }}>
                Go to Print Tab to Print These
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

// ─── CSS ─────────────────────────────────────────────────────────────────────
const moduleCSS = `
.sm-wrap { display:flex; flex-direction:column; height:100%; overflow:hidden; font-family:'Inter',sans-serif; background:#f0f2f5 }

/* Tab bar */
.sm-tabbar { display:flex; align-items:center; background:#fff; border-bottom:2px solid #DEE2E6; flex-shrink:0; padding:0 16px; gap:2px }
.sm-tab { padding:10px 16px; border:none; background:none; font-size:13px; font-weight:600; color:#6C757D; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-2px; transition:all .12s; white-space:nowrap }
.sm-tab:hover { color:#1565C0; background:#eff6ff }
.sm-tab.on { color:#1565C0; border-bottom-color:#1565C0 }
.sm-style-sel { margin-left:auto; display:flex; gap:8px; align-items:center }

/* Body */
.sm-body { flex:1; overflow-y:auto; padding:16px 20px }

/* Scan layout */
.scan-layout { display:grid; grid-template-columns:320px 1fr; gap:16px; min-height:0 }
.scan-left  { display:flex; flex-direction:column; gap:8px }
.scan-right { overflow-y:auto; background:#fff; border:1px solid #DEE2E6; border-radius:8px; padding:12px }

/* Step indicator */
.scan-steps { display:flex; flex-direction:column; gap:4px }
.scan-step { display:flex; align-items:center; gap:8px; padding:7px 10px; border-radius:7px; border:1.5px solid #DEE2E6; background:#F8F9FA; transition:all .15s }
.scan-step.active { border-width:2px }
.scan-step.done { opacity:.65 }
.step-num { width:20px; height:20px; border-radius:50%; background:#CED4DA; color:#fff; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; flex-shrink:0 }
.step-lbl { font-size:12px; font-weight:600; color:#2C3E50 }

/* Context panel */
.ctx-panel { background:#F8F9FA; border:1px solid #DEE2E6; border-radius:7px; padding:8px 10px }
.ctx-row { display:flex; justify-content:space-between; padding:3px 0; border-bottom:1px solid #F4F6F8; font-size:11px }
.ctx-row:last-child { border-bottom:none }
.ctx-row.set .ctx-val { color:#15803d; font-weight:700 }
.ctx-key { color:#ADB5BD }
.ctx-val { color:#2C3E50; font-family:monospace; font-size:11px }

/* Step prompt */
.step-prompt { border:1.5px solid; border-radius:7px; padding:9px 12px; margin-top:2px }

/* Scan input */
.scan-inp { font-size:15px !important }

/* Mode toggle: Production vs Lost Time */
.mode-toggle { display:flex; gap:4px; margin-bottom:4px }
.mode-btn {
  flex:1; padding:8px; border-radius:7px; border:1.5px solid #DEE2E6;
  background:#F8F9FA; color:#6C757D; font-size:12px; font-weight:700; cursor:pointer;
}
.mode-btn.on { background:#1A2A3A; color:#fff; border-color:#1A2A3A }

.lt-banner {
  background:#eef2ff; border:1px solid #c7d2fe; color:#4338ca;
  font-size:11px; line-height:1.5; padding:8px 10px; border-radius:7px; margin-bottom:4px;
}

/* Scan log / invalid log tabs */
.log-tab {
  padding:5px 10px; border-radius:6px; border:1px solid #DEE2E6;
  background:#F8F9FA; color:#6C757D; font-size:11px; font-weight:700; cursor:pointer;
}
.log-tab.on { background:#1A2A3A; color:#fff; border-color:#1A2A3A }

/* Eff panel */
.eff-panel { background:#fff; border:1px solid #DEE2E6; border-radius:8px; padding:12px; margin-top:4px }

/* Section header */
.sec-hdr { font-size:12px; font-weight:700; color:#495057; margin-bottom:8px; padding-bottom:5px; border-bottom:1px solid #DEE2E6; display:flex; align-items:center; gap:6px; flex-wrap:wrap }

/* Info grid for style summary */
.info-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:8px; margin-bottom:12px }
.info-cell { background:#F8F9FA; border:1px solid #DEE2E6; border-radius:5px; padding:6px 8px }
.info-lbl { font-size:10px; color:#ADB5BD; text-transform:uppercase; letter-spacing:.4px; margin-bottom:2px }
.info-val { font-size:13px; font-weight:600; color:#0D1B2A }

/* Print — A4 precise */
@media print {
  .sm-tabbar, .no-print { display:none !important }
  .sm-body { padding:0 !important }
  body { margin:0; background:#fff !important }
  #print-area { display:block !important }
  @page { size: A4 portrait; margin: 10mm; }
}

/* Garment QR grid — compact, many small heat-transfer QR labels per page */
.garment-qr-grid {
  display:flex; flex-wrap:wrap; gap:6px;
}
.garment-qr-cell {
  border:1px dashed #CED4DA; border-radius:6px; padding:6px;
  text-align:center; background:#fff; width:110px;
}
.garment-qr-meta {
  font-size:8px; color:#495057; margin-top:3px; line-height:1.3;
  font-family:monospace;
}
#garment-print-area { display:none; }
@media print {
  #garment-print-area {
    display:flex !important; flex-wrap:wrap; gap:4mm;
  }
  #garment-print-area .garment-qr-cell {
    border:0.5px solid #999; width:26mm;
    page-break-inside:avoid;
  }
}

/* A4 sheet container — print-accurate width. Screen preview gets a decorative
   OUTER wrapper so its padding doesn't eat into the actual printable area. */
.a4-sheet {
  box-sizing: border-box;
  display:flex; flex-wrap:wrap; gap:6mm;
  width:190mm; margin:0 auto; background:#fff;
}
@media screen {
  .a4-sheet {
    border:1px dashed #CED4DA; box-shadow:0 2px 8px rgba(0,0,0,.06);
    /* padding goes around the 190mm box via an outer margin trick so the
       190mm content area itself stays untouched and two 92mm tickets fit */
    margin:10mm auto;
    padding:10mm;
    width:190mm;
    /* expand box to 190mm + 20mm padding so inner content area is still 190mm */
    box-sizing: content-box;
  }
}
@media print {
  .a4-sheet { padding:0; margin:0; width:auto; }
}

/* Bundle ticket — ONE per row at full 190mm width for proper barcode size
   Barcode cells get ~44mm each at 4 per row — reliably scannable on inkjet */
.bundle-ticket {
  width:190mm; border:1.2px solid #000; font-family:'Courier New',monospace;
  font-size:7.5pt; background:#fff; position:relative;
  page-break-inside:avoid; break-inside:avoid; margin-bottom:5mm;
}
.ticket-badge {
  position:absolute; top:-1px; left:-1px; width:16px; height:16px;
  background:#000; color:#fff; font-size:8pt; font-weight:700;
  display:flex; align-items:center; justify-content:center;
}

/* Operation barcode strip — horizontal cells like the sample's R/S Clr | T/T Clr | Irn Clr Pt */
.ticket-ops-row { display:flex; border-bottom:1px solid #000; }
.ticket-op-cell {
  flex:1; min-width:0; border-right:1px solid #000; padding:2px 2px 2px;
  text-align:center; overflow:hidden;
}
.ticket-op-cell:last-child { border-right:none; }
.ticket-op-abbr { font-size:5.8pt; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:1px; }
.ticket-op-barcode { line-height:0; margin:1px auto; display:flex; justify-content:center; overflow:hidden; }
.ticket-op-barcode svg { display:block; max-width:100%; height:48px; width:auto; }
.ticket-op-num { font-size:5pt; letter-spacing:-0.2px; white-space:nowrap; overflow:hidden; margin-top:1px; }
.ticket-op-meta { display:flex; justify-content:space-between; font-size:4.8pt; color:#555; padding:0 1px; }

/* Info table footer — 6 columns: 3 label/value pairs per row, sums to exactly 100% */
.ticket-info { width:100%; border-collapse:collapse; font-size:6.5pt; table-layout:fixed; }
.ticket-info td {
  border:0.5px solid #999; padding:1px 2.5px; vertical-align:middle;
  overflow:hidden; white-space:nowrap; text-overflow:ellipsis;
  line-height:1.25;
}
.ti-lbl { font-weight:700; background:#f4f4f4; width:13%; font-size:5.8pt; }
.ti-val { width:20.3%; }
.ti-style { font-weight:700; }
.ti-order { font-size:5.8pt; }
.ti-num { text-align:center; }

/* Screen-only preview tint so tickets are easy to see before printing */
@media screen {
  .bundle-ticket { box-shadow:0 1px 3px rgba(0,0,0,.08); }
}

/* Re-use from parent */
.card { background:#fff; border:1px solid #DEE2E6; border-radius:8px; padding:18px; margin-bottom:14px; box-shadow:0 1px 3px rgba(0,0,0,.04) }
.card h3 { font-size:13px; font-weight:700; color:#0D1B2A; margin-bottom:12px; padding-bottom:9px; border-bottom:1px solid #DEE2E6; display:flex; align-items:center; gap:8px; flex-wrap:wrap }
.form-row { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; margin-bottom:12px }
.fg { display:flex; flex-direction:column; gap:4px }
.fl { font-size:11px; font-weight:500; color:#6C757D }
.fl.new { color:#b45309; font-weight:700 }
.inp,.sel { background:#F8F9FA; border:1px solid #CED4DA; color:#0D1B2A; font-family:'Inter',sans-serif; font-size:13px; padding:7px 10px; border-radius:6px; outline:none }
.inp:focus,.sel:focus { border-color:#1565C0; box-shadow:0 0 0 3px rgba(37,99,235,.1) }
.inp.new-field,.sel.new-field { border:2px solid #FFB300; background:#FFF8E1 }
.btn { padding:7px 14px; border:none; border-radius:6px; font-family:'Inter',sans-serif; font-size:13px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:6px; transition:all .15s }
.btn-p { background:#1565C0; color:#fff }
.btn-p:hover { background:#0D47A1 }
.btn-s { background:#F8F9FA; color:#2C3E50; border:1px solid #CED4DA }
.btn-cam { background:#0D1B2A; color:#fff; justify-content:center }
.btn-cam:hover { background:#1A2A3A }

/* Camera scanner overlay */
.cam-overlay {
  position:fixed; inset:0; background:rgba(0,0,0,.75); z-index:9999;
  display:flex; align-items:center; justify-content:center; padding:16px;
}
.cam-modal {
  background:#0D1B2A; border-radius:12px; padding:14px; width:100%; max-width:420px;
  box-shadow:0 20px 60px rgba(0,0,0,.5);
}
.cam-hdr {
  display:flex; justify-content:space-between; align-items:center;
  color:#fff; font-size:14px; font-weight:700; margin-bottom:10px;
}
.cam-close {
  background:#1A2A3A; border:none; color:#fff; width:28px; height:28px;
  border-radius:6px; cursor:pointer; font-size:14px;
}
.cam-region {
  width:100%; border-radius:8px; overflow:hidden; background:#111;
  min-height:260px; position:relative;
}
.cam-video {
  width:100%; height:auto; display:block;
  object-fit:cover; border-radius:8px;
}
.cam-region canvas { display:none !important; }
.cam-spinner {
  position:absolute; inset:0; display:flex; align-items:center;
  justify-content:center; color:#6C757D; font-size:13px;
}
/* Guide box — visually matches Quagga's area:{top:30%,bottom:30%,left:10%,right:10%}
   Only barcodes inside this box get decoded, so the visual must match exactly */
.cam-guide-box {
  position:absolute; top:30%; bottom:30%; left:10%; right:10%;
  border:2.5px solid rgba(74,222,128,0.9);
  border-radius:6px;
  box-shadow:0 0 0 2000px rgba(0,0,0,0.35);
  pointer-events:none;
}
.cam-guide-hint {
  position:absolute; bottom:calc(30% - 22px); left:0; right:0;
  text-align:center; color:#4ade80; font-size:11px; font-weight:700;
  text-shadow:0 1px 2px rgba(0,0,0,0.8); pointer-events:none;
}
.cam-status {
  color:#ADB5BD; font-size:12px; text-align:center; padding:20px 10px;
}
.cam-status.ok  { color:#4ade80; padding:10px 0 0; }
.cam-status.err { color:#EF9A9A; padding:20px 10px; }

/* Batch scan accumulation panel */
.cam-batch-panel { margin-top:8px; padding:10px; background:#0D1B2A; border-radius:8px; }
.cam-batch-chips { display:flex; flex-wrap:wrap; gap:5px; max-height:120px; overflow-y:auto; }
.cam-chip {
  display:inline-flex; align-items:center; gap:5px;
  background:#052e16; color:#4ade80; border:1px solid #166534;
  border-radius:14px; padding:3px 8px 3px 10px; font-size:10px; font-family:monospace;
}
.cam-chip button {
  background:none; border:none; color:#4ade80; cursor:pointer;
  font-size:11px; padding:0; opacity:0.7;
}
.cam-chip button:hover { opacity:1; }
.btn-g { background:#388E3C; color:#fff }
.btn:disabled { opacity:.4; cursor:default }
.tbl { width:100%; border-collapse:separate; border-spacing:0; font-size:13px; border:1px solid #DEE2E6; border-radius:7px; overflow:hidden }
.tbl th { background:#F8F9FA; padding:8px 12px; text-align:left; font-size:11px; font-weight:700; color:#6C757D; letter-spacing:.3px; text-transform:uppercase; border-bottom:2px solid #DEE2E6 }
.tbl td { padding:7px 12px; border-bottom:1px solid #DEE2E6; color:#0D1B2A }
.tbl tr:last-child td { border-bottom:none }
.tbl tr:hover td { background:#f5f8ff }
.stats { display:flex; background:#fff; border:1px solid #DEE2E6; border-radius:8px; overflow:hidden; margin-bottom:14px }
.stat-b { flex:1; padding:11px 16px; border-right:1px solid #DEE2E6 }
.stat-b:last-child { border-right:none }
.stat-l { font-size:10px; color:#6C757D; font-weight:600; text-transform:uppercase; letter-spacing:.4px; margin-bottom:2px }
.stat-v { font-size:20px; font-weight:700; font-family:'JetBrains Mono',monospace; line-height:1.1 }
.stat-s { font-size:11px; color:#6C757D; margin-top:2px }
.pill { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:700 }
.pill-g  { background:#C8E6C9; color:#388E3C }
.pill-w  { background:#FFF8E1; color:#E65100 }
.pill-r  { background:#FFCDD2; color:#C62828 }
.pill-a  { background:#eff6ff; color:#1565C0 }
.pill-2  { background:#EDE7F6; color:#6A1B9A }
.pill-am { background:#FFF8E1; color:#b45309 }
.bc-grid { display:flex; flex-wrap:wrap; gap:10px; padding:4px 0 }
.fetched-block { background:#eff6ff; border:1px solid #90CAF9; border-radius:7px; padding:12px 14px; margin-bottom:12px }
.block-hdr { font-size:11px; font-weight:700; color:#0D47A1; margin-bottom:10px }
.new-block { background:#FFF8E1; border:2px solid #FFB300; border-radius:7px; padding:12px 14px; margin-bottom:12px }
.new-block .block-hdr { color:#b45309 }
.src-badge { display:inline-flex; align-items:center; font-size:10px; font-weight:700; padding:2px 7px; border-radius:20px; border:1px solid }
.alert { padding:10px 14px; border-radius:6px; font-size:13px; margin-bottom:12px; display:flex; align-items:center; gap:8px }
.alert.ok   { background:#C8E6C9; color:#15803d; border:1px solid #86efac }
.alert.err  { background:#FFCDD2; color:#C62828; border:1px solid #fca5a5 }
.alert.inf  { background:#eff6ff; color:#1565C0; border:1px solid #90CAF9 }
.alert.warn { background:#FFF8E1; color:#E65100; border:1px solid #fcd34d }
.spin { width:16px; height:16px; border:2px solid #CED4DA; border-top-color:#1565C0; border-radius:50%; animation:rsp .6s linear infinite; display:inline-block }
@keyframes rsp { to { transform:rotate(360deg) } }
.empty-st { text-align:center; padding:48px; color:#6C757D }
.empty-st .ei { font-size:40px; opacity:.2; margin-bottom:8px }
.empty-st .et { font-size:13px }
`;
